import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import { useDarkMode } from 'shared/context/DarkModeContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import API from 'shared/api';
import {
  FiPlus, FiEdit2, FiTrash2, FiRefreshCw, FiLayers, FiBox, FiTool,
  FiBarChart2, FiX, FiSearch, FiAlertTriangle, FiArrowRight, FiBookOpen,
  FiHash, FiTag, FiDollarSign, FiInfo, FiPackage, FiGitBranch,
  FiClock, FiMapPin, FiPlay, FiCheck, FiChevronDown, FiChevronUp,
  FiList, FiTrendingUp, FiExternalLink, FiXCircle, FiShoppingBag, FiDownload,
} from 'react-icons/fi';

const money = (n) => {
  const v = Number(n || 0);
  return `$${v.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const num = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('es-VE', { maximumFractionDigits: 3 });
};

// Formatea una cantidad con la unidad/simbolo del insumo (ej: "1.5 kg").
const fmtCant = (cant, simbolo) => `${num(cant)}${simbolo ? ' ' + simbolo : ''}`;

// Genera un código sugerido a partir del nombre (ej: "Acero inoxidable" -> "ACEROINO").
const generarCodigo = (nombre) =>
  nombre
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9\s]/g, '')
    .trim()
    .toUpperCase()
    .split(/\s+/)
    .map((w) => w.slice(0, 4))
    .join('')
    .slice(0, 10) || 'INS';

const TABS = [
  { id: 'insumos', label: 'Insumos', permiso: 'produccion.seccion.insumos' },
  { id: 'recetas', label: 'Recetas', permiso: 'produccion.seccion.recetas' },
  { id: 'ordenes', label: 'Órdenes de Producción', permiso: 'produccion.seccion.ordenes' },
  { id: 'gastos', label: 'Movimientos', permiso: 'produccion.seccion.gastos' },
];

const GUIA_KEY = 'agorax_prod_guia_v1';

export default function Produccion({ onNavigate }) {
  const { isAdmin, isPlanExterno, tienePermiso } = useAuth();
  const { COL } = useDarkMode();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const tp = (c) => tienePermiso(c);
  const v = (c) => tienePermiso(c);
  const puedeGestionar = (isAdmin() || tp('produccion.gestionar')) && !isPlanExterno();
  const puedeMover = (isAdmin() || tp('produccion.ajustar_insumos')) && !isPlanExterno();
  const puedeOrdenes = (isAdmin() || tp('produccion.ordenes')) && !isPlanExterno();

  const [tab, setTab] = useState('insumos');
  const [sedes, setSedes] = useState([]);
  const [sede, setSede] = useState('');
  const [guiaAbierta, setGuiaAbierta] = useState(() => {
    try { return localStorage.getItem(GUIA_KEY) !== 'oculta'; } catch { return true; }
  });

  const loadSedes = useCallback(async () => {
    try {
      const r = await API.get('/sedes/listar');
      const list = r.data?.data || [];
      setSedes(list);
      const activa = list.find((s) => s.activa) || list[0];
      if (activa) setSede((prev) => prev || activa.codigo);
    } catch (e) {
      console.error('Error cargando sedes', e);
    }
  }, []);

  useEffect(() => {
    loadSedes();
  }, [loadSedes]);

  const ocultarGuia = () => {
    setGuiaAbierta(false);
    try { localStorage.setItem(GUIA_KEY, 'oculta'); } catch {}
  };

  return (
    <div style={{ padding: isMobile ? 12 : 20, maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 22, fontWeight: 700, color: COL.text }}>Producción</h2>
          <div style={{ fontSize: 13, color: COL.muted, marginTop: 3 }}>
            Insumos, recetas, producción y costos
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {!guiaAbierta && (
            <Boton COL={COL} ghost onClick={() => setGuiaAbierta(true)}>
              <FiBookOpen size={14} /> Guía
            </Boton>
          )}
          <SedeSelector sedes={sedes} sede={sede} setSede={setSede} COL={COL} isMobile={isMobile} />
        </div>
      </div>

      {isPlanExterno() && (
        <div style={{ ...S.banner, ...S.warnBanner }}>
          <FiAlertTriangle size={16} style={{ color: COL.warningText }} />
          <span>Tu plan es externo: la producción es de solo lectura.</span>
        </div>
      )}

      {guiaAbierta && (
        <GuiaInicio COL={COL} isMobile={isMobile} onClose={ocultarGuia} onIr={(t) => setTab(t)} puedeGestionar={puedeGestionar} />
      )}

      <Resumen sede={sede} COL={COL} v={v} isMobile={isMobile} />

      <div style={{ display: 'flex', gap: 4, margin: '16px 0', borderBottom: `1px solid ${COL.border}`, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {TABS.filter((t) => isAdmin() || tp(t.permiso)).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: isMobile ? '12px 14px' : '10px 16px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? COL.pri2 : COL.muted,
              borderBottom: tab === t.id ? `2px solid ${COL.pri2}` : '2px solid transparent',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'insumos' && (
        <InsumosTab sede={sede} sedes={sedes} COL={COL} puedeGestionar={puedeGestionar} puedeMover={puedeMover} isMobile={isMobile} />
      )}
      {tab === 'recetas' && (
        <RecetasTab sede={sede} COL={COL} puedeGestionar={puedeGestionar} isMobile={isMobile} />
      )}
      {tab === 'ordenes' && (
        <OrdenesTab sede={sede} sedes={sedes} COL={COL} puedeOrdenes={puedeOrdenes} isMobile={isMobile} />
      )}
      {tab === 'gastos' && (
        <GastosTab sede={sede} COL={COL} v={v} isMobile={isMobile} puedeMover={puedeMover} onNavigate={onNavigate} />
      )}
    </div>
  );
}

function GuiaInicio({ COL, isMobile, onClose, onIr, puedeGestionar }) {
  const pasos = [
    { n: 1, title: 'Registra tus insumos', desc: 'Crea los insumos (materia prima, componentes, materiales…) con su unidad, costo y stock mínimo. Luego ingresa su stock con "Mover stock".', tab: 'insumos', cta: 'Ir a Insumos' },
    { n: 2, title: 'Define tus recetas', desc: 'Indica qué insumos y cantidades componen cada producto o subproducto. El sistema calcula el costo automáticamente.', tab: 'recetas', cta: 'Ir a Recetas' },
    { n: 3, title: 'Ordena y produce', desc: 'Envía una orden de producción para que otro usuario la ejecute. Sigue su línea de tiempo y costos en la pestaña Órdenes de Producción.', tab: 'ordenes', cta: 'Ir a Órdenes' },
  ];

  return (
    <div style={{ background: COL.blueBg, borderRadius: 14, border: `1px solid ${COL.border}`, padding: isMobile ? 14 : 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: COL.pri2, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FiBookOpen size={18} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: COL.text }}>¿Cómo funciona la producción?</div>
            <div style={{ fontSize: 12.5, color: COL.muted }}>Tres pasos para empezar. Puedes cerrar esta guía cuando quieras.</div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.faint, padding: 4 }}>
          <FiX size={16} />
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12, marginTop: 16 }}>
        {pasos.map((p) => (
          <div key={p.n} style={{ background: COL.surface, borderRadius: 12, border: `1px solid ${COL.border}`, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: COL.pri2, color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{p.n}</span>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: COL.text }}>{p.title}</span>
            </div>
            <div style={{ fontSize: 12.5, color: COL.muted, lineHeight: 1.5, flex: 1 }}>{p.desc}</div>
            {puedeGestionar && (
              <button onClick={() => onIr(p.tab)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: COL.pri2, fontSize: 12.5, fontWeight: 700, padding: 0, alignSelf: 'flex-start' }}>
                {p.cta} <FiArrowRight size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SedeSelector({ sedes, sede, setSede, COL, isMobile }) {
  if (sedes.length === 0) return null;
  return (
    <select
      value={sede}
      onChange={(e) => setSede(e.target.value)}
      style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, fontSize: 13, cursor: 'pointer', maxWidth: isMobile ? 160 : 220 }}
    >
      {sedes.map((s) => (
        <option key={s.codigo} value={s.codigo}>{s.nombre || s.codigo}</option>
      ))}
    </select>
  );
}

function Resumen({ sede, COL, v, isMobile }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!sede) return;
    Promise.all([
      API.get(`/produccion/insumos/stock?sede=${sede}`),
      API.get('/produccion/recetas?activa=true&limite=1'),
      API.get(`/produccion/alertas-insumos?sede=${sede}`),
    ])
      .then(([stock, recetas, alertas]) => {
        const items = stock.data?.data || [];
        const valor = items.reduce((s, i) => s + Number(i.stock || 0) * Number(i.costo_unitario || 0), 0);
        setData({ total: items.length, valor, alertas: (alertas.data?.data || []).length });
      })
      .catch((e) => console.error('Error resumen', e));
  }, [sede]);

  const cards = [
    { label: 'Insumos', value: data ? data.total : '…', icon: FiBox, color: COL.pri2, permiso: 'vis.produccion.stock_insumos' },
    { label: 'Valor insumos', value: data ? money(data.valor) : '…', icon: FiBarChart2, color: COL.green, permiso: 'vis.produccion.costo' },
    { label: 'Bajo mínimo', value: data ? data.alertas : '…', icon: FiAlertTriangle, color: data && data.alertas > 0 ? COL.red : COL.amber, permiso: 'vis.produccion.stock_insumos' },
  ].filter((c) => v(c.permiso));

  if (cards.length === 0) return null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: isMobile ? 8 : 12 }}>
      {cards.map((c) => (
        <div key={c.label} style={{ ...S.card, background: COL.surface, borderColor: COL.border, alignItems: isMobile ? 'center' : 'flex-start', textAlign: isMobile ? 'center' : 'left', gap: 6, padding: isMobile ? 10 : 14 }}>
          <c.icon size={isMobile ? 16 : 18} style={{ color: c.color }} />
          <div style={{ width: '100%' }}>
            <div style={{ fontSize: isMobile ? 10.5 : 12, color: COL.muted }}>{c.label}</div>
            <div style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: COL.text }}>{c.value}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ icon: Icon, title, desc, COL, actionLabel, onAction }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 16px', background: COL.surface, borderRadius: 14, border: `1px dashed ${COL.border}` }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: COL.blueBg, color: COL.pri2, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
        <Icon size={26} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: COL.text }}>{title}</div>
      <div style={{ fontSize: 13, color: COL.muted, marginTop: 6, maxWidth: 420, margin: '6px auto 0', lineHeight: 1.5 }}>{desc}</div>
      {actionLabel && onAction && (
        <div style={{ marginTop: 16 }}>
          <Boton COL={COL} onClick={onAction}><FiPlus size={15} /> {actionLabel}</Boton>
        </div>
      )}
    </div>
  );
}

function InsumosTab({ sede, sedes, COL, puedeGestionar, puedeMover, isMobile }) {
  const [insumos, setInsumos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [recetas, setRecetas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [modal, setModal] = useState(null);
  const [moverModal, setMoverModal] = useState(null);
  const [catsModal, setCatsModal] = useState(false);

  const load = useCallback(async () => {
    if (!sede) return;
    setCargando(true);
    try {
      const [stock, cats, units, recs] = await Promise.all([
        API.get(`/produccion/insumos/stock?sede=${sede}`),
        API.get('/produccion/categorias'),
        API.get('/produccion/unidades'),
        API.get('/produccion/recetas?activa=true&limite=200'),
      ]);
      setInsumos(stock.data?.data || []);
      setCategorias(cats.data?.data || []);
      setUnidades(units.data?.data || []);
      setRecetas(recs.data?.data?.items || []);
    } catch (e) {
      console.error('Error insumos', e);
    } finally {
      setCargando(false);
    }
  }, [sede]);

  useEffect(() => { load(); }, [load]);

  const eliminar = async (id) => {
    if (!window.confirm('¿Desactivar este insumo?')) return;
    await API.delete(`/produccion/insumos/${id}`);
    load();
  };

  const catName = (id) => categorias.find((c) => c.id === id)?.nombre || '—';
  const unitName = (id) => unidades.find((u) => u.id === id)?.nombre || '—';
  const unidadDe = (i) => i.unidad_simbolo || i.unidad_nombre || unitName(i.unidad_medida_id);
  const stockBajo = (i) => Number(i.stock) <= Number(i.stock_minimo);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {puedeGestionar && <Boton COL={COL} onClick={() => setModal({ tipo: 'crear' })}><FiPlus size={14} /> Nuevo insumo</Boton>}
        {puedeMover && <Boton COL={COL} onClick={() => setMoverModal({})}><FiRefreshCw size={14} /> Mover stock</Boton>}
        {puedeGestionar && <Boton COL={COL} ghost onClick={() => setCatsModal(true)}><FiLayers size={14} /> Categorías y unidades</Boton>}
      </div>

      {!cargando && insumos.length === 0 ? (
        <EmptyState icon={FiBox} title="Aún no tienes insumos" desc="Los insumos son los materiales o componentes que usas para producir (materia prima, piezas, componentes…). Crea el primero para empezar a controlar tu producción." COL={COL} actionLabel={puedeGestionar ? 'Crear mi primer insumo' : null} onAction={puedeGestionar ? () => setModal({ tipo: 'crear' }) : null} />
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {insumos.map((i) => (
            <div key={i.id} style={{ ...S.card, background: COL.surface, borderColor: COL.border, flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: COL.text, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.nombre}</div>
                <div style={{ fontSize: 11.5, color: COL.faint, marginTop: 2 }}>{i.codigo} · {catName(i.categoria_id)} · {unitName(i.unidad_medida_id)}</div>
                <div style={{ fontSize: 12, color: COL.muted, marginTop: 2 }}>Costo {money(i.costo_unitario)}/{unidadDe(i) || 'un'}</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 11, color: COL.muted }}>Stock</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: stockBajo(i) ? COL.red : COL.text }}>{num(i.stock)} <span style={{ fontSize: 11, fontWeight: 600, color: COL.muted }}>{unidadDe(i)}</span></div>
                {puedeGestionar && (
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
                    <IconBtn COL={COL} onClick={() => setModal({ tipo: 'editar', insumo: i })}><FiEdit2 size={14} /></IconBtn>
                    <IconBtn COL={COL} danger onClick={() => eliminar(i.id)}><FiTrash2 size={14} /></IconBtn>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Tabla>
          <thead>
            <tr>
              <th style={TH}>Código</th>
              <th style={TH}>Nombre</th>
              <th style={TH}>Categoría</th>
              <th style={TH}>Unidad</th>
              <th style={TH}>Costo</th>
              <th style={TH}>Mínimo</th>
              <th style={TH}>Objetivo</th>
              <th style={TH}>Stock</th>
              {puedeGestionar && <th style={TH}></th>}
            </tr>
          </thead>
          <tbody>
            {insumos.map((i) => (
              <tr key={i.id} style={{ borderBottom: `1px solid ${COL.border}` }}>
                <td style={TD}>{i.codigo}</td>
                <td style={TD}><strong>{i.nombre}</strong></td>
                <td style={TD}>{catName(i.categoria_id)}</td>
                <td style={TD}>{unitName(i.unidad_medida_id)}</td>
                <td style={TD}>{money(i.costo_unitario)}/{unidadDe(i) || 'un'}</td>
                <td style={TD}>{num(i.stock_minimo)} {unidadDe(i)}</td>
                <td style={TD}>{i.stock_objetivo ? `${num(i.stock_objetivo)} ${unidadDe(i)}` : '—'}</td>
                <td style={{ ...TD, fontWeight: 700, color: stockBajo(i) ? COL.red : COL.text }}>{num(i.stock)} <span style={{ fontWeight: 600, color: COL.muted }}>{unidadDe(i)}</span></td>
                {puedeGestionar && (
                  <td style={TD}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <IconBtn COL={COL} onClick={() => setModal({ tipo: 'editar', insumo: i })}><FiEdit2 size={14} /></IconBtn>
                      <IconBtn COL={COL} danger onClick={() => eliminar(i.id)}><FiTrash2 size={14} /></IconBtn>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}

      {modal && (
        <InsumoModal insumo={modal.insumo} categorias={categorias} unidades={unidades} recetas={recetas} COL={COL} onClose={() => setModal(null)} onSaved={load} isMobile={isMobile} />
      )}
      {moverModal && (
        <MoverModal insumos={insumos} categorias={categorias} sede={sede} COL={COL} onClose={() => setMoverModal(null)} onSaved={load} isMobile={isMobile} />
      )}
      {catsModal && (
        <CatsModal categorias={categorias} unidades={unidades} COL={COL} onClose={() => setCatsModal(false)} onRefresh={load} isMobile={isMobile} />
      )}
    </div>
  );
}

function RecetasTab({ sede, COL, puedeGestionar, isMobile }) {
  const [recetas, setRecetas] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [productos, setProductos] = useState([]);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, i, p] = await Promise.all([
        API.get('/produccion/recetas?limite=200'),
        API.get(`/produccion/insumos/stock?sede=${sede}`),
        API.get('/produccion/productos?limite=200'),
      ]);
      setRecetas(r.data?.data?.items || []);
      setInsumos(i.data?.data || []);
      setProductos(p.data?.data || []);
    } catch (e) {
      console.error('Error recetas', e);
    }
  }, [sede]);

  useEffect(() => { load(); }, [load]);

  const eliminar = async (id) => {
    if (!window.confirm('¿Desactivar esta receta?')) return;
    await API.delete(`/produccion/recetas/${id}`);
    load();
  };

  const abrir = async (receta) => {
    if (!receta) { setModal({ receta: null }); return; }
    try {
      const r = await API.get(`/produccion/recetas/${receta.id}`);
      setModal({ receta: r.data?.data });
    } catch (e) { console.error(e); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {puedeGestionar && <Boton COL={COL} onClick={() => abrir(null)}><FiPlus size={14} /> Nueva receta</Boton>}
      </div>

      {recetas.length === 0 ? (
        <EmptyState icon={FiLayers} title="Aún no tienes recetas" desc="Una receta define qué insumos y cantidades componen un producto o un subproducto intermedio. Al producirla, el sistema descuenta insumos y calcula su costo." COL={COL} actionLabel={puedeGestionar ? 'Crear mi primera receta' : null} onAction={puedeGestionar ? () => abrir(null) : null} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {recetas.map((r) => (
            <div key={r.id} style={{ ...S.card, background: COL.surface, borderColor: COL.border, cursor: puedeGestionar ? 'pointer' : 'default' }} onClick={() => puedeGestionar && abrir(r)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ color: COL.text }}>{r.nombre}</strong>
                  <div style={{ fontSize: 12, color: COL.muted }}>
                    {r.resultado_nombre ? `Produce: ${r.resultado_nombre}` : 'Sin resultado asignado'}
                  </div>
                  {r.rendimiento != null && r.rendimiento > 1 && (
                    <div style={{ fontSize: 11, color: COL.faint }}>Rinde {r.rendimiento} unidades por receta</div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 13 }}>
                <span style={{ color: COL.muted }}>Costo unit.</span>
                {r.costo_estimado != null && <span style={{ color: COL.green, fontWeight: 600 }}>{money(r.costo_estimado)}/un</span>}
              </div>
              {puedeGestionar && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                  <IconBtn COL={COL} danger onClick={() => eliminar(r.id)}><FiTrash2 size={14} /></IconBtn>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modal && (
        <RecetaModal receta={modal.receta} insumos={insumos} productos={productos} COL={COL} onClose={() => setModal(null)} onRefresh={load} isMobile={isMobile} />
      )}
    </div>
  );
}

const TIPO_GASTO_UI = {
  consumo: { label: 'Consumo', color: '#ea580c' },
  merma: { label: 'Merma', color: '#dc2626' },
  elaboracion: { label: 'Elaboración', color: '#2563eb' },
  venta: { label: 'Venta', color: '#16a34a' },
};

function GastosTab({ sede, COL, v, isMobile, puedeMover, onNavigate }) {
  const [gastos, setGastos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [categoria, setCategoria] = useState('');
  const [tipo, setTipo] = useState('');
  const [q, setQ] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [cargando, setCargando] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [ultimoId, setUltimoId] = useState(null);
  const [expandidos, setExpandidos] = useState({});
  const [modal, setModal] = useState(false);
  const [exportando, setExportando] = useState(false);

  const verCosto = v('vis.produccion.costo');

  const filtros = useMemo(() => {
    const p = new URLSearchParams();
    if (sede) p.set('sede', sede);
    if (categoria) p.set('categoria_id', categoria);
    if (tipo) p.set('tipo', tipo);
    if (q.trim()) p.set('q', q.trim());
    if (desde) p.set('desde', desde);
    if (hasta) p.set('hasta', hasta);
    return p.toString();
  }, [sede, categoria, tipo, q, desde, hasta]);

  const load = useCallback(async () => {
    if (!sede || !verCosto) return;
    setCargando(true);
    try {
      const [g, r, c] = await Promise.all([
        API.get(`/produccion/gastos?${filtros}`),
        API.get(`/produccion/gastos/resumen?${filtros}`),
        API.get('/produccion/categorias'),
      ]);
      const items = g.data?.data?.items || [];
      setGastos(items);
      setHasMore(!!g.data?.data?.has_more);
      setUltimoId(items.length ? items[items.length - 1].id : null);
      setResumen(r.data?.data || null);
      setCategorias(c.data?.data || []);
    } catch (e) { console.error(e); }
    finally { setCargando(false); }
  }, [sede, verCosto, filtros]);

  useEffect(() => { load(); }, [load]);

  const cargarMas = async () => {
    setCargandoMas(true);
    const p = new URLSearchParams(filtros);
    if (ultimoId) p.set('ultimo_id', ultimoId);
    try {
      const g = await API.get(`/produccion/gastos?${p.toString()}`);
      const items = g.data?.data?.items || [];
      setGastos((prev) => [...prev, ...items]);
      setHasMore(!!g.data?.data?.has_more);
      if (items.length) setUltimoId(items[items.length - 1].id);
    } catch (e) { console.error(e); }
    finally { setCargandoMas(false); }
  };

  const toggle = (id) => setExpandidos((p) => ({ ...p, [id]: !p[id] }));

  const exportar = async () => {
    setExportando(true);
    try {
      const params = new URLSearchParams(filtros);
      const r = await API.get('/produccion/gastos/exportar', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'produccion_movimientos.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setExportando(false); }
  };

  const verPedido = (ref) => {
    if (!ref || !onNavigate) return;
    try {
      if (ref.tipo === 'pedido') {
        const esTienda = ref.tipo_pedido === 'tienda' || ref.tipo_pedido === 'mesa';
        localStorage.setItem('agorax_pedido_abrir', JSON.stringify({ id: ref.id, vista: esTienda ? 'tienda' : 'online' }));
        if (esTienda) {
          onNavigate('pedidos-tienda');
        } else {
          localStorage.setItem('agorax_worker_tab', JSON.stringify('historial'));
          onNavigate('chats');
        }
      } else if (ref.tipo === 'venta') {
        onNavigate('cierre-caja-admin');
      }
    } catch (e) { console.error(e); }
  };

  if (!verCosto) {
    return <EmptyState icon={FiBarChart2} title="Sin permisos para ver costos" desc="No tienes permiso para ver costos y movimientos. Contacta a un administrador." COL={COL} />;
  }

  const kpis = [
    { label: 'Costo total', value: resumen ? money(resumen.total) : '…', icon: FiDollarSign, color: COL.red },
    { label: 'Movimientos', value: resumen ? String(resumen.movimientos) : '…', icon: FiList, color: COL.pri2 },
    { label: 'Insumos afectados', value: resumen ? String(resumen.insumos_afectados) : '…', icon: FiBox, color: COL.green },
    { label: 'Top categoría', value: resumen && resumen.por_categoria?.[0] ? resumen.por_categoria[0].categoria : '—', icon: FiLayers, color: COL.amber, sub: resumen && resumen.por_categoria?.[0] ? money(resumen.por_categoria[0].costo_total) : null },
  ];

  const maxCat = resumen?.por_categoria?.reduce((m, x) => Math.max(m, Number(x.costo_total || 0)), 0) || 0;
  const maxTipo = resumen?.por_tipo?.reduce((m, x) => Math.max(m, Number(x.costo_total || 0)), 0) || 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {puedeMover && <Boton COL={COL} onClick={() => setModal(true)}><FiPlus size={14} /> Nuevo movimiento</Boton>}
        <Boton COL={COL} ghost onClick={exportar} disabled={exportando}>
          <FiDownload size={14} /> {exportando ? 'Exportando…' : 'Exportar CSV'}
        </Boton>
        <button
          onClick={() => setTipo(tipo === 'venta' ? '' : 'venta')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8,
            border: `1px solid ${tipo === 'venta' ? COL.pri2 : COL.border}`, cursor: 'pointer',
            background: tipo === 'venta' ? COL.blueBg : 'transparent', color: tipo === 'venta' ? COL.pri2 : COL.text,
            fontSize: 12.5, fontWeight: 700, minHeight: 36,
          }}
        >
          <FiShoppingBag size={13} /> Solo ventas
        </button>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={{ ...selectStyle(COL), width: isMobile ? '100%' : 150 }}>
          <option value="">Todos los tipos</option>
          {Object.entries(TIPO_GASTO_UI).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
        </select>
        <select value={categoria} onChange={(e) => setCategoria(e.target.value)} style={{ ...selectStyle(COL), width: isMobile ? '100%' : 180 }}>
          <option value="">Todas las categorías</option>
          {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={{ ...inputStyle(COL), width: isMobile ? 'calc(50% - 4px)' : 140 }} />
        <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={{ ...inputStyle(COL), width: isMobile ? 'calc(50% - 4px)' : 140 }} />
        <div style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '1 1 180px', minWidth: 140 }}>
          <FiSearch size={14} style={{ position: 'absolute', left: 10, top: 11, color: COL.faint }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar insumo…" style={{ ...inputStyle(COL), paddingLeft: 30 }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(160px, 1fr))', gap: isMobile ? 8 : 12, marginBottom: 16 }}>
        {kpis.map((k) => (
          <div key={k.label} style={{ ...S.card, background: COL.surface, borderColor: COL.border, alignItems: 'flex-start', gap: 6, padding: isMobile ? 12 : 14, minWidth: 0 }}>
            <k.icon size={isMobile ? 16 : 18} style={{ color: k.color }} />
            <div style={{ width: '100%', minWidth: 0 }}>
              <div style={{ fontSize: isMobile ? 10.5 : 12, color: COL.muted }}>{k.label}</div>
              <div style={{ fontSize: isMobile ? 14 : 17, fontWeight: 800, color: COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.value}</div>
              {k.sub && <div style={{ fontSize: isMobile ? 10 : 11, color: COL.faint }}>{k.sub}</div>}
            </div>
          </div>
        ))}
      </div>

      {resumen && resumen.por_categoria?.length > 0 && (
        <div style={{ background: COL.surface, borderRadius: 14, border: `1px solid ${COL.border}`, padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <FiTrendingUp size={14} style={{ color: COL.pri2 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>Costo por categoría</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {resumen.por_categoria.slice(0, 6).map((c) => {
              const pct = maxCat > 0 ? Math.round((Number(c.costo_total || 0) / maxCat) * 100) : 0;
              return (
                <div key={c.categoria} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: isMobile ? 90 : 130, fontSize: 12, color: COL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{c.categoria}</span>
                  <div style={{ flex: 1, height: 10, borderRadius: 6, background: COL.bg2, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 6, background: COL.pri2, minWidth: pct > 0 ? 4 : 0 }} />
                  </div>
                  <span style={{ width: 70, textAlign: 'right', fontSize: 12, fontWeight: 700, color: COL.text, flexShrink: 0 }}>{money(c.costo_total)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {resumen && resumen.por_tipo?.length > 0 && (
        <div style={{ background: COL.surface, borderRadius: 14, border: `1px solid ${COL.border}`, padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <FiList size={14} style={{ color: COL.pri2 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>Costo por tipo</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {resumen.por_tipo.map((t) => {
              const cfg = TIPO_GASTO_UI[t.tipo] || { label: t.tipo, color: COL.pri2 };
              const pct = maxTipo > 0 ? Math.round((Number(t.costo_total || 0) / maxTipo) * 100) : 0;
              return (
                <div key={t.tipo} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: isMobile ? 90 : 130, fontSize: 12, color: COL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{cfg.label}</span>
                  <div style={{ flex: 1, height: 10, borderRadius: 6, background: COL.bg2, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 6, background: cfg.color, minWidth: pct > 0 ? 4 : 0 }} />
                  </div>
                  <span style={{ width: 70, textAlign: 'right', fontSize: 12, fontWeight: 700, color: COL.text, flexShrink: 0 }}>{money(t.costo_total)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {cargando && gastos.length === 0 ? (
        <div style={{ padding: '40px 18px', textAlign: 'center', fontSize: 13, color: COL.faint }}>Cargando movimientos…</div>
      ) : gastos.length === 0 ? (
        <EmptyState icon={FiBarChart2} title="Sin movimientos registrados" desc="Registra un movimiento descontando un insumo (simple o compuesto) para llevar el control de tus costos de producción." COL={COL} actionLabel={puedeMover ? 'Registrar mi primer movimiento' : null} onAction={puedeMover ? () => setModal(true) : null} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {gastos.map((g) => {
            const cfg = TIPO_GASTO_UI[g.tipo] || { label: g.tipo, color: COL.muted };
            const abierto = !!expandidos[g.id];
            return (
              <div key={g.id} style={{ background: COL.surface, borderRadius: 14, border: `1px solid ${COL.border}`, overflow: 'hidden' }}>
                <div onClick={() => toggle(g.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, cursor: 'pointer' }}>
                  <span style={{ ...S.badge, background: hexA(cfg.color, 0.14), color: cfg.color, display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    {cfg.label}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {g.items?.map((it) => it.nombre).join(', ') || `Movimiento #${g.id}`}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 3 }}>
                      {g.creado_en && (
                        <span style={{ fontSize: 11.5, color: COL.faint, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <FiClock size={11} /> {new Date(g.creado_en).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' })} · {new Date(g.creado_en).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      {g.categoria_nombre && <span style={{ fontSize: 11.5, color: COL.muted }}>{g.categoria_nombre}</span>}
                      <span style={{ fontSize: 11.5, color: COL.faint }}>{g.items?.length || 0} insumo{(g.items?.length || 0) !== 1 ? 's' : ''}</span>
                    </div>
                    {g.motivo && <div style={{ fontSize: 12, color: COL.muted, marginTop: 3, fontStyle: 'italic' }}>{g.motivo}</div>}
                    {g.tipo === 'venta' && g.referencia && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 3 }}>
                        <span style={{ fontSize: 11.5, color: COL.pri2, fontWeight: 700 }}>
                          {g.referencia.tipo === 'pedido' ? `Pedido #${g.referencia.id}` : `Venta #${g.referencia.id}`}
                        </span>
                        {g.referencia.cliente_nombre && <span style={{ fontSize: 11.5, color: COL.muted }}>{g.referencia.cliente_nombre}</span>}
                        {g.referencia.cancelado && (
                          <span style={{ ...S.badge, background: COL.redBg, color: COL.red, fontSize: 10, padding: '2px 7px', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            <FiXCircle size={11} /> cancelado
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: COL.red }}>{money(g.costo_total)}</div>
                  </div>
                  <div style={{ color: COL.faint, flexShrink: 0 }}>
                    {abierto ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                  </div>
                </div>

                {abierto && (
                  <div style={{ borderTop: `1px solid ${COL.border}`, background: COL.bg2, padding: '10px 14px 14px' }}>
                    {g.items?.length ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {g.items.map((it) => (
                          <div key={it.insumo_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, flexWrap: 'wrap' }}>
                            <FiBox size={12} style={{ color: COL.faint, flexShrink: 0 }} />
                            <span style={{ flex: 1, minWidth: 120, color: COL.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.nombre}</span>
                            <span style={{ color: COL.faint, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11, whiteSpace: 'nowrap' }}>{it.codigo}</span>
                            {it.tipo === 'compuesto' && <span style={{ ...S.badge, background: COL.blueBg, color: COL.infoText, fontSize: 10, padding: '1px 6px' }}>compuesto</span>}
                            <span style={{ color: COL.red, fontWeight: 700, whiteSpace: 'nowrap' }}>−{num(Math.abs(it.cantidad))}</span>
                            <span style={{ color: COL.muted, whiteSpace: 'nowrap' }}>{money(it.costo_unitario)}/un</span>
                            <span style={{ color: COL.text, fontWeight: 700, whiteSpace: 'nowrap', minWidth: 70, textAlign: 'right' }}>{money(it.costo_total)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: COL.faint }}>Sin detalle de insumos.</div>
                    )}
                    {g.notas && <div style={{ fontSize: 11.5, color: COL.faint, marginTop: 8, borderTop: `1px dashed ${COL.border}`, paddingTop: 8 }}>Nota: {g.notas}</div>}
                    {g.tipo === 'venta' && g.referencia && (
                      <div style={{ borderTop: `1px solid ${COL.border}`, paddingTop: 10, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.faint, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {g.referencia.tipo === 'pedido' ? 'Pedido relacionado' : 'Venta relacionada'}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 12.5, color: COL.muted }}>
                          <span><strong style={{ color: COL.text }}>#{g.referencia.id}</strong></span>
                          {g.referencia.cliente_nombre && <span>Cliente: {g.referencia.cliente_nombre}</span>}
                          {g.referencia.cliente_telefono && <span>{g.referencia.cliente_telefono}</span>}
                          {g.referencia.tipo === 'pedido' && g.referencia.tipo_entrega && <span>{g.referencia.tipo_entrega}</span>}
                          <span>Total: {money(Number(g.referencia.total || 0) + Number(g.referencia.costo_envio || 0))}</span>
                          {g.referencia.fecha && <span>{new Date(g.referencia.fecha).toLocaleString('es-VE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                        </div>
                        {g.referencia.cancelado && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: COL.redBg, color: COL.red, borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 700 }}>
                            <FiXCircle size={14} /> Pedido cancelado
                            {g.referencia.motivo_cancelacion && <span style={{ fontWeight: 400 }}>· {g.referencia.motivo_cancelacion}</span>}
                          </div>
                        )}
                        {onNavigate && g.referencia.tipo === 'pedido' && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Boton COL={COL} ghost onClick={() => verPedido(g.referencia)}>
                              <FiExternalLink size={13} /> Ver pedido en historial
                            </Boton>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
          <Boton COL={COL} ghost onClick={cargarMas} disabled={cargandoMas}>
            {cargandoMas ? 'Cargando…' : 'Cargar más'}
          </Boton>
        </div>
      )}

      {modal && (
        <GastoModal sede={sede} categorias={categorias} COL={COL} onClose={() => setModal(false)} onSaved={load} isMobile={isMobile} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
//  MODALES
// ════════════════════════════════════════════════════════════════════

const ESTADOS_ORDEN_UI = {
  borrador: { label: 'Borrador', color: '#6b7280', icon: FiEdit2 },
  planificada: { label: 'Planificada', color: '#2563eb', icon: FiClock },
  en_proceso: { label: 'En proceso', color: '#ea580c', icon: FiPlay },
  completada: { label: 'Completada', color: '#16a34a', icon: FiCheck },
};

const hexA = (hex, alpha) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const TRANSICIONES_ORDEN_UI = {
  borrador: ['planificada'],
  planificada: ['en_proceso', 'borrador'],
  en_proceso: ['completada'],
  completada: [],
};

const EVENTO_ORDEN_UI = {
  creacion: { label: 'Orden creada', color: '#2563eb' },
  transicion: { label: 'Cambio de fase', color: '#ea580c' },
  ejecucion: { label: 'Producción ejecutada', color: '#16a34a' },
  reversion: { label: 'Producción revertida', color: '#6b7280' },
};

function OrdenesTab({ sede, sedes, COL, puedeOrdenes, isMobile }) {
  const [ordenes, setOrdenes] = useState([]);
  const [recetas, setRecetas] = useState([]);
  const [modal, setModal] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [completar, setCompletar] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (sede) params.set('sede', sede);
      const [o, r] = await Promise.all([
        API.get(`/produccion/ordenes?${params.toString()}`),
        API.get('/produccion/recetas?activa=true&limite=200'),
      ]);
      setOrdenes(o.data?.data?.items || []);
      setRecetas(r.data?.data?.items || []);
    } catch (e) { console.error(e); }
  }, [sede]);

  useEffect(() => { load(); }, [load]);

  const cambiarEstado = async (id, estado) => {
    try {
      await API.post(`/produccion/ordenes/${id}/estado`, { estado });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error cambiando estado');
    }
  };

  const pedirCompletar = (o) => {
    setDetalle(null);
    setCompletar(o);
  };

  const revertir = async (o) => {
    if (!window.confirm(`¿Revertir la orden #${o.id}? Se devolverán los insumos y se descontará el resultado producido. La merma asociada se eliminará.`)) return;
    try {
      await API.post(`/produccion/ordenes/${o.id}/revertir`);
      setDetalle(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al revertir la orden');
    }
  };

  const abrirDetalle = async (id) => {
    setCargandoDetalle(true);
    setDetalle({ id });
    try {
      const r = await API.get(`/produccion/ordenes/${id}`);
      setDetalle({ id, ...(r.data?.data || {}) });
    } catch (e) {
      console.error(e);
      setDetalle(null);
    } finally {
      setCargandoDetalle(false);
    }
  };

  const eliminar = async (o) => {
    if (!window.confirm(`¿Eliminar la orden #${o.id}${o.receta_nombre ? ` (${o.receta_nombre})` : ''}? Esta acción no se puede deshacer.`)) return;
    try {
      await API.delete(`/produccion/ordenes/${o.id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error eliminando la orden');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {puedeOrdenes && <Boton COL={COL} onClick={() => setModal(true)}><FiPlus size={14} /> Nueva orden</Boton>}
      </div>

      {ordenes.length === 0 ? (
        <EmptyState icon={FiTool} title="Sin órdenes de producción" desc="Envía una orden interna para que otro usuario la ejecute. Se selecciona la receta y la cantidad se calcula según el stock de insumos disponible." COL={COL} actionLabel={puedeOrdenes ? 'Crear mi primera orden' : null} onAction={puedeOrdenes ? () => setModal(true) : null} />
      ) : (
        <KanbanOrdenes ordenes={ordenes} COL={COL} isMobile={isMobile} puedeActuar={puedeOrdenes} onTransicion={cambiarEstado} onCompletar={pedirCompletar} onRevertir={revertir} onVer={abrirDetalle} onEliminar={eliminar} />
      )}

      {modal && <OrdenModal recetas={recetas} sedes={sedes} COL={COL} onClose={() => setModal(false)} onSaved={load} isMobile={isMobile} />}

      {detalle && (
        <OrdenDetalleModal
          detalle={detalle}
          cargando={cargandoDetalle}
          COL={COL}
          isMobile={isMobile}
          puedeActuar={puedeOrdenes}
          onClose={() => setDetalle(null)}
          onTransicion={cambiarEstado}
          onCompletar={pedirCompletar}
          onRevertir={revertir}
        />
      )}

      {completar && (
        <CompletarProduccionModal
          orden={completar}
          COL={COL}
          isMobile={isMobile}
          onClose={() => setCompletar(null)}
          onSaved={() => { setCompletar(null); load(); }}
        />
      )}
    </div>
  );
}

function KanbanOrdenes({ ordenes, COL, isMobile, puedeActuar, onTransicion, onCompletar, onRevertir, onVer, onEliminar }) {
  const columnas = [
    { id: 'borrador', label: 'Borrador', desc: 'Pendientes de planificar' },
    { id: 'planificada', label: 'Planificada', desc: 'Listas para ejecutar' },
    { id: 'en_proceso', label: 'En proceso', desc: 'Ejecutándose ahora' },
    { id: 'completada', label: 'Completada', desc: 'Producción terminada' },
  ];
  const porEstado = (estado) => ordenes.filter((o) => o.estado === estado);
  const colBase = isMobile ? '84vw' : '280px';

  return (
    <div style={{ display: 'flex', gap: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 12, alignItems: 'stretch' }}>
      {columnas.map((col) => {
        const cfg = ESTADOS_ORDEN_UI[col.id];
        const items = porEstado(col.id);
        const StateIcon = cfg.icon;
        return (
          <div key={col.id} style={{ flex: `0 0 ${colBase}`, width: colBase, background: COL.bg2, borderRadius: 16, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 260 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 6px 10px', borderBottom: `2px solid ${hexA(cfg.color, 0.35)}` }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: hexA(cfg.color, 0.15), color: cfg.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <StateIcon size={16} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: COL.text, lineHeight: 1.1 }}>{col.label}</div>
                <div style={{ fontSize: 10.5, color: COL.faint, marginTop: 2 }}>{col.desc}</div>
              </div>
              <span style={{ fontSize: 14, fontWeight: 800, color: cfg.color, background: hexA(cfg.color, 0.14), borderRadius: 20, padding: '2px 10px', minWidth: 24, textAlign: 'center' }}>{items.length}</span>
            </div>

            {items.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: COL.faint, border: `1.5px dashed ${COL.border}`, borderRadius: 12, padding: 20, textAlign: 'center' }}>Sin órdenes</div>
            ) : items.map((o) => (
              <div key={o.id} onClick={() => onVer && onVer(o.id)} style={{ background: COL.surface, border: `1px solid ${COL.border}`, borderLeft: `4px solid ${cfg.color}`, borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', transition: 'transform 0.12s, box-shadow 0.12s' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: COL.blueBg, color: COL.pri2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FiLayers size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: COL.text, fontSize: 13.5, lineHeight: 1.25, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{o.receta_nombre || `Receta #${o.receta_id}`}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
                      <span style={{ ...S.badge, background: hexA(cfg.color, 0.14), color: cfg.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <StateIcon size={11} /> {cfg.label}
                      </span>
                      <span style={{ fontSize: 11, color: COL.faint, fontWeight: 600 }}>#{o.id}</span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 22, height: 22, borderRadius: 6, background: COL.bg2, color: COL.muted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiMapPin size={12} /></span>
                    <span style={{ fontSize: 11.5, color: COL.muted, fontWeight: 600 }}>{o.sede_codigo}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                    <span style={{ width: 22, height: 22, borderRadius: 6, background: COL.bg2, color: COL.muted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiHash size={12} /></span>
                    <span style={{ fontSize: 13, color: COL.text, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{o.estado === 'completada' && o.cantidad_resultante != null ? num(o.cantidad_resultante) : num(o.cantidad)}</span>
                    <span style={{ fontSize: 10.5, color: COL.faint }}>{o.estado === 'completada' && o.cantidad_resultante != null ? 'un' : 'un esp.'}</span>
                  </div>
                </div>

                {o.creado_en && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiClock size={11} style={{ color: COL.faint }} />
                    <span style={{ fontSize: 10.5, color: COL.faint }}>{new Date(o.creado_en).toLocaleDateString('es-VE', { day: '2-digit', month: 'short' })}{' · '}{new Date(o.creado_en).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                )}

                {puedeActuar && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', borderTop: `1px solid ${COL.border}`, paddingTop: 10, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                    {(TRANSICIONES_ORDEN_UI[o.estado] || []).map((e2) => {
                      const tcfg = ESTADOS_ORDEN_UI[e2];
                      const TIcon = tcfg.icon;
                      const esCompletar = e2 === 'completada';
                      return (
                        <button key={e2} onClick={() => (esCompletar ? onCompletar(o) : onTransicion(o.id, e2))} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: hexA(tcfg.color, 0.12), color: tcfg.color, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, padding: '5px 9px', borderRadius: 7 }}>
                          <TIcon size={11} /> {esCompletar ? 'Completar' : tcfg.label}
                        </button>
                      );
                    })}
                    {o.estado === 'completada' && onRevertir && (
                      <button onClick={() => onRevertir(o)} title="Revertir producción" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: COL.faint, padding: 5, borderRadius: 7, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700 }}>
                        <FiRefreshCw size={12} /> Revertir
                      </button>
                    )}
                    {onEliminar && o.estado !== 'completada' && (
                      <button onClick={() => onEliminar(o)} title="Eliminar orden" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: COL.faint, padding: 5, borderRadius: 7, display: 'inline-flex', alignItems: 'center' }}>
                        <FiTrash2 size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function OrdenDetalleModal({ detalle, cargando, COL, isMobile, puedeActuar, onClose, onTransicion, onCompletar, onRevertir }) {
  const orden = detalle?.orden;
  const receta = detalle?.receta;
  const historial = detalle?.historial || [];

  const fmtFecha = (d) => (d ? new Date(d).toLocaleString('es-VE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
  const unidadDe = (ins) => ins?.unidad_simbolo || ins?.unidad_nombre || '';

  return (
    <Overlay COL={COL} onClose={onClose} isMobile={isMobile}>
      <div style={{ ...S.modal, background: COL.surface, maxWidth: 640 }}>
        <ModalHeader icon={FiTool} title={orden ? (orden.receta_nombre || `Orden #${orden.id}`) : `Orden #${detalle?.id || ''}`} subtitle={orden ? `#${orden.id} · ${orden.sede_codigo} · ${num(orden.cantidad)} unidades` : 'Cargando…'} onClose={onClose} COL={COL} />

        {cargando ? (
          <div style={{ padding: '40px 18px', textAlign: 'center', fontSize: 13, color: COL.faint }}>Cargando detalle…</div>
        ) : !orden ? (
          <div style={{ padding: '40px 18px', textAlign: 'center', fontSize: 13, color: COL.faint }}>No se pudo cargar la orden.</div>
        ) : (
          <div style={{ padding: '14px 18px 6px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ ...S.badge, background: COL.bg2, color: ESTADOS_ORDEN_UI[orden.estado]?.color || COL.text, fontSize: 12, padding: '4px 10px' }}>
                {ESTADOS_ORDEN_UI[orden.estado]?.label || orden.estado}
              </span>
              {orden.notas && <span style={{ fontSize: 12.5, color: COL.muted }}>{orden.notas}</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)', gap: 8 }}>
              <div style={{ background: COL.blueBg, border: `1px solid ${COL.border}`, borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: COL.faint, textTransform: 'uppercase', letterSpacing: 0.4 }}>Cantidad esperada</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: COL.pri2, lineHeight: 1.2 }}>
                  {num(orden.cantidad)} <span style={{ fontSize: 13, fontWeight: 600 }}>un</span>
                </div>
              </div>
              <div style={{ background: COL.bg2, border: `1px solid ${COL.border}`, borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: COL.faint, textTransform: 'uppercase', letterSpacing: 0.4 }}>Resultado de la producción</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: orden.estado === 'completada' ? COL.green : COL.faint, lineHeight: 1.2 }}>
                  {orden.estado === 'completada' && orden.cantidad_resultante != null ? (
                    <>{num(orden.cantidad_resultante)} <span style={{ fontSize: 13, fontWeight: 600 }}>un</span></>
                  ) : (
                    <span style={{ fontSize: 15, fontWeight: 600 }}>—</span>
                  )}
                </div>
                {orden.estado === 'completada' && orden.merma > 0 && (
                  <div style={{ fontSize: 11, color: COL.red, marginTop: 2 }}>merma {num(orden.merma)} un</div>
                )}
              </div>
            </div>

            {puedeActuar && orden.estado !== 'completada' && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(TRANSICIONES_ORDEN_UI[orden.estado] || []).map((e2) => {
                  const esCompletar = e2 === 'completada';
                  return (
                    <Boton key={e2} COL={COL} ghost={!esCompletar} onClick={() => { if (esCompletar) { onCompletar(orden); } else { onTransicion(orden.id, e2); onClose(); } }}>
                      {esCompletar ? '▶ Completar producción' : `→ ${ESTADOS_ORDEN_UI[e2]?.label}`}
                    </Boton>
                  );
                })}
              </div>
            )}

            {puedeActuar && orden.estado === 'completada' && onRevertir && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Boton COL={COL} ghost onClick={() => onRevertir(orden)}><FiRefreshCw size={13} /> Revertir producción</Boton>
              </div>
            )}

            <div>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.faint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Receta completa</div>
              {receta && receta.insumos && receta.insumos.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {receta.insumos.map((ins) => (
                    <div key={ins.insumo_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: COL.bg2, borderRadius: 8, fontSize: 12.5 }}>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COL.text, fontWeight: 600 }}>{ins.nombre}</span>
                      <span style={{ color: COL.faint, whiteSpace: 'nowrap', fontSize: 11 }}>{ins.codigo}</span>
                      <span style={{ color: COL.pri2, whiteSpace: 'nowrap', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                        {num(ins.cantidad)} {unidadDe(ins)}
                      </span>
                      {ins.merma ? (
                        <span style={{ ...S.badge, background: COL.amberBg, color: COL.amber, fontSize: 10.5, padding: '2px 7px', whiteSpace: 'nowrap' }}>merma {num(ins.merma)}%</span>
                      ) : null}
                      <span style={{ color: COL.muted, whiteSpace: 'nowrap', fontWeight: 600 }}>{money(ins.costo_unitario)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: COL.faint }}>Esta receta no tiene insumos registrados.</div>
              )}
            </div>

            <div>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.faint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Línea de tiempo</div>
              {historial.length === 0 ? (
                <div style={{ fontSize: 12.5, color: COL.faint }}>Sin eventos registrados.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {historial.map((h, i) => {
                    const ev = EVENTO_ORDEN_UI[h.evento] || { label: h.evento, color: COL.muted };
                    const esUltimo = i === 0;
                    return (
                      <div key={h.id || i} style={{ display: 'flex', gap: 10 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: ev.color, flexShrink: 0, marginTop: 4 }} />
                          {i < historial.length - 1 && <span style={{ width: 2, flex: 1, background: COL.border, marginTop: 2 }} />}
                        </div>
                        <div style={{ flex: 1, paddingBottom: 14, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: esUltimo ? 700 : 600, color: COL.text }}>{ev.label}</span>
                            {h.estado_nuevo && <span style={{ fontSize: 11, color: COL.muted }}>→ {ESTADOS_ORDEN_UI[h.estado_nuevo]?.label || h.estado_nuevo}</span>}
                          </div>
                          <div style={{ fontSize: 11.5, color: COL.muted, marginTop: 2 }}>
                            {h.actor_nombre ? `${h.actor_nombre}${h.actor_cedula ? ` (${h.actor_cedula})` : ''}` : (h.actor_cedula || 'Sistema')} · {fmtFecha(h.creado_en)}
                          </div>
                          {h.notas && <div style={{ fontSize: 11.5, color: COL.faint, marginTop: 2 }}>{h.notas}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Overlay>
  );
}

function CompletarProduccionModal({ orden, COL, isMobile, onClose, onSaved }) {
  const esperada = Number(orden?.cantidad) || 0;
  const [resultante, setResultante] = useState(String(esperada));
  const [merma, setMerma] = useState('0');
  const [categoriaId, setCategoriaId] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [errors, setErrors] = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    API.get('/produccion/categorias').then((r) => setCategorias(r.data?.data || [])).catch(() => {});
  }, []);

  const res = Number(resultante) || 0;
  const mer = Number(merma) || 0;

  const onResultante = (v) => {
    setResultante(v);
    setMerma(String(Math.max(0, esperada - (Number(v) || 0))));
  };
  const onMerma = (v) => {
    setMerma(v);
    setResultante(String(Math.max(0, esperada - (Number(v) || 0))));
  };

  const guardar = async () => {
    const e = {};
    if (resultante === '' || res < 0) e.resultante = 'Indica una cantidad válida';
    if (res > esperada) e.resultante = 'No puede superar la cantidad esperada';
    if (mer < 0) e.merma = 'No puede ser negativa';
    if (mer > 0 && !categoriaId) e.categoriaId = 'Selecciona la categoría de merma';
    setErrors(e);
    if (Object.keys(e).length) return;

    setGuardando(true);
    try {
      await API.post(`/produccion/ordenes/${orden.id}/ejecutar`, {
        cantidad_resultante: res,
        categoria_merma_id: categoriaId ? Number(categoriaId) : null,
      });
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === 'object' && d.faltantes) {
        const f0 = d.faltantes[0];
        setErrors({ general: `Stock insuficiente de "${f0.nombre}" (requerido ${f0.requerido}, disponible ${f0.disponible})` });
      } else {
        setErrors({ general: typeof d === 'string' ? d : 'No se pudo completar la orden.' });
      }
      setGuardando(false);
    }
  };

  return (
    <Overlay COL={COL} onClose={onClose} isMobile={isMobile}>
      <div style={{ ...S.modal, background: COL.surface, maxWidth: 520 }}>
        <ModalHeader icon={FiCheck} title="Completar producción" subtitle={orden?.receta_nombre || `Orden #${orden?.id}`} onClose={onClose} COL={COL} />

        <div style={{ padding: '18px 18px 6px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: COL.blueBg, border: `1px solid ${COL.border}`, borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: COL.muted }}>Cantidad esperada</span>
            <span style={{ fontSize: 22, fontWeight: 800, color: COL.pri2 }}>{num(esperada)} <span style={{ fontSize: 13, fontWeight: 600 }}>un</span></span>
          </div>

          <Field label="Cantidad resultante" icon={FiHash} required error={errors.resultante} hint="¿Cuántas unidades se produjeron realmente?" COL={COL}>
            <input type="number" inputMode="decimal" step="0.01" min="0" max={esperada} value={resultante} onChange={(e) => onResultante(e.target.value)} autoFocus style={inputStyle(COL)} />
          </Field>

          <Field label="Merma" icon={FiAlertTriangle} error={errors.merma} hint="Diferencia entre lo esperado y lo producido. Se registra como gasto." COL={COL}>
            <input type="number" inputMode="decimal" step="0.01" min="0" value={merma} onChange={(e) => onMerma(e.target.value)} style={{ ...inputStyle(COL), color: mer > 0 ? COL.red : undefined, fontWeight: mer > 0 ? 700 : undefined }} />
          </Field>

          {mer > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: COL.redBg, border: `1px solid ${COL.border}`, borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: COL.red, fontWeight: 700 }}>
                <FiAlertTriangle size={14} /> Merma de {num(mer)} unidades
              </div>
              <div style={{ fontSize: 12, color: COL.muted }}>El costo de la merma se registrará como gasto. Ajusta la merma si es necesario.</div>
              <Field label="Categoría de merma" icon={FiLayers} required error={errors.categoriaId} COL={COL}>
                <select value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)} style={selectStyle(COL)}>
                  <option value="">Seleccionar…</option>
                  {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </Field>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: COL.bg2, borderRadius: 10, padding: '10px 14px', fontSize: 12.5, color: COL.muted }}>
            <span>Resumen</span>
            <span>Resultante <strong style={{ color: COL.text }}>{num(res)}</strong> · Merma <strong style={{ color: mer > 0 ? COL.red : COL.text }}>{num(mer)}</strong></span>
          </div>
        </div>

        {errors.general && <div style={{ padding: '0 18px', color: COL.red, fontSize: 12.5 }}>{errors.general}</div>}

        <ModalFooter onClose={onClose} COL={COL} onSave={guardar} guardando={guardando} label="Completar producción" />
      </div>
    </Overlay>
  );
}

function OrdenModal({ recetas, sedes, COL, onClose, onSaved, isMobile }) {
  const [paso, setPaso] = useState(1); // 1 receta/sede · 2 cantidad · 3 resumen
  const [recetaId, setRecetaId] = useState('');
  const [sede, setSede] = useState(sedes[0]?.codigo || '');
  const [modo, setModo] = useState('unidades'); // 'unidades' | 'ingrediente'
  const [cantidad, setCantidad] = useState('');
  const [insumoRef, setInsumoRef] = useState('');
  const [cantRef, setCantRef] = useState('');
  const [capacidad, setCapacidad] = useState(null);
  const [cargandoCap, setCargandoCap] = useState(false);
  const [notas, setNotas] = useState('');
  const [errors, setErrors] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [picker, setPicker] = useState(null); // 'receta' | 'sede' | 'insumo'

  useEffect(() => {
    if (!recetaId || !sede) { setCapacidad(null); return; }
    let activo = true;
    setCargandoCap(true);
    API.get(`/produccion/recetas/${recetaId}/capacidad?sede=${encodeURIComponent(sede)}`)
      .then((r) => { if (activo) setCapacidad(r.data?.data || null); })
      .catch(() => { if (activo) setCapacidad(null); })
      .finally(() => { if (activo) setCargandoCap(false); });
    return () => { activo = false; };
  }, [recetaId, sede]);

  const maxProducciones = capacidad?.max_producciones ?? 0;
  const costoUnitario = capacidad?.costo_estimado ?? null;
  const recetaSel = recetas.find((r) => String(r.id) === String(recetaId)) || null;
  const sedeSel = sedes.find((s) => s.codigo === sede) || null;

  const insumoRefInfo = capacidad?.insumos?.find((x) => String(x.insumo_id) === String(insumoRef)) || null;
  const reqRef = insumoRefInfo ? Number(insumoRefInfo.requerido_por_unidad) : 0;
  const stockRef = insumoRefInfo ? Number(insumoRefInfo.stock) : 0;
  const unidadesRef = (reqRef > 0 && Number(cantRef) > 0) ? Math.floor(Number(cantRef) / reqRef) : 0;
  const unidadesEfectivas = modo === 'ingrediente'
    ? (maxProducciones > 0 ? Math.min(unidadesRef, maxProducciones) : 0)
    : (maxProducciones > 0 ? Math.min(Number(cantidad) || 0, maxProducciones) : (Number(cantidad) || 0));
  const limitadoPorOtros = modo === 'ingrediente' && unidadesRef > 0 && maxProducciones > 0 && unidadesRef > maxProducciones;
  const costoTotal = costoUnitario != null && unidadesEfectivas > 0 ? costoUnitario * unidadesEfectivas : null;

  const ajustarCantidad = () => {
    if (modo === 'unidades' && maxProducciones > 0 && Number(cantidad) > maxProducciones) {
      setCantidad(String(maxProducciones));
    }
  };
  const ajustarCantRef = () => {
    if (insumoRefInfo && Number(cantRef) > stockRef) {
      setCantRef(String(stockRef));
    }
  };

  const validar = () => {
    const e = {};
    if (!recetaId) e.recetaId = 'Selecciona una receta';
    if (modo === 'ingrediente') {
      if (!insumoRef) e.insumoRef = 'Selecciona un ingrediente de referencia';
      if (!(Number(cantRef) > 0)) e.cantRef = 'Debe ser mayor a 0';
      else if (insumoRefInfo && Number(cantRef) > stockRef) e.cantRef = `Supera el stock (${fmtCant(stockRef, insumoRefInfo.unidad_simbolo)})`;
      else if (maxProducciones === 0) e.cantRef = 'Stock insuficiente para producir';
      else if (unidadesEfectivas <= 0) e.cantRef = 'La cantidad no alcanza para 1 unidad';
    } else {
      if (!Number(cantidad) || Number(cantidad) <= 0) e.cantidad = 'Debe ser mayor a 0';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const continuar = () => {
    if (paso === 1) {
      const e = {};
      if (!recetaId) e.recetaId = 'Selecciona una receta';
      setErrors(e);
      if (Object.keys(e).length) return;
      setPaso(2);
    } else if (paso === 2) {
      if (validar()) setPaso(3);
    }
  };

  const guardar = async () => {
    if (!validar()) return;
    setGuardando(true);
    try {
      await API.post('/produccion/ordenes', {
        receta_id: Number(recetaId), sede, cantidad: unidadesEfectivas,
        notas: notas.trim() || null,
      });
      onClose();
      onSaved();
    } catch (err) {
      setErrors({ general: err?.response?.data?.detail || 'No se pudo crear la orden.' });
      setGuardando(false);
    }
  };

  const PASOS = [
    { n: 1, label: 'Receta' },
    { n: 2, label: 'Cantidad' },
    { n: 3, label: 'Resumen' },
  ];

  return (
    <Overlay COL={COL} onClose={onClose} isMobile={isMobile}>
      <div style={{ ...S.modal, background: COL.surface, maxWidth: 540 }}>
        <ModalHeader icon={FiTool} title="Nueva orden de producción" subtitle="Sigue los pasos para enviar una orden interna" onClose={onClose} COL={COL} />

        <div style={{ display: 'flex', gap: 4, padding: '12px 18px 0', alignItems: 'center' }}>
          {PASOS.map((p, i) => (
            <div key={p.n} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ flex: 1, height: 3, borderRadius: 2, background: paso > i ? COL.pri2 : (paso === p.n ? COL.pri2 : COL.border) }} />
                {i < PASOS.length - 1 && <div style={{ flex: 1, height: 3, borderRadius: 2, background: paso > p.n ? COL.pri2 : COL.border }} />}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                <span style={{ width: 20, height: 20, borderRadius: '50%', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', background: paso >= p.n ? COL.pri2 : COL.bg2, color: paso >= p.n ? '#fff' : COL.faint }}>{p.n}</span>
                <span style={{ fontSize: 11, fontWeight: paso >= p.n ? 700 : 500, color: paso >= p.n ? COL.text : COL.faint, whiteSpace: 'nowrap' }}>{p.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: '16px 18px 6px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {paso === 1 && (
            <>
              <Field label="Receta" icon={FiLayers} required error={errors.recetaId} COL={COL}>
                <PickerTrigger COL={COL} icon={FiLayers} error={!!errors.recetaId} onClick={() => setPicker('receta')}>
                  {recetaSel ? (
                    <>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COL.text }}>{recetaSel.nombre}</span>
                      <FiCheck size={14} style={{ flexShrink: 0, color: COL.green }} />
                    </>
                  ) : (
                    <span style={{ flex: 1, color: COL.muted }}>Seleccionar receta…</span>
                  )}
                </PickerTrigger>
              </Field>

              <Field label="Sede" icon={FiPackage} COL={COL}>
                <PickerTrigger COL={COL} icon={FiPackage} onClick={() => setPicker('sede')}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COL.text }}>{sedeSel ? (sedeSel.nombre || sedeSel.codigo) : 'Seleccionar sede…'}</span>
                  {sedeSel && <FiCheck size={14} style={{ flexShrink: 0, color: COL.green }} />}
                </PickerTrigger>
              </Field>

              {recetaId && (
                <div style={{ background: COL.bg2, borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {cargandoCap ? (
                    <span style={{ fontSize: 12, color: COL.faint }}>Calculando stock…</span>
                  ) : capacidad ? (
                    <>
                      <div style={{ textAlign: 'center', padding: '2px 0 6px' }}>
                        <div style={{ fontSize: 10.5, fontWeight: 800, color: COL.faint, textTransform: 'uppercase', letterSpacing: 0.5 }}>Capacidad máxima</div>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 6, marginTop: 2 }}>
                          <span style={{ fontSize: 34, fontWeight: 800, color: maxProducciones > 0 ? COL.green : COL.red, lineHeight: 1 }}>{maxProducciones}</span>
                          <span style={{ fontSize: 13, color: COL.muted }}>unidades</span>
                        </div>
                      </div>

                      {capacidad.insumos.length === 0 ? (
                        <span style={{ fontSize: 12, color: COL.faint }}>Esta receta no tiene insumos definidos.</span>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {capacidad.insumos.map((ins) => {
                            const unidad = ins.unidad_simbolo || ins.unidad_nombre || '';
                            const esCuello = ins.alcanza === maxProducciones && maxProducciones > 0;
                            const req = Number(ins.requerido_por_unidad);
                            const consumoAlMax = maxProducciones * req;
                            const pct = Number(ins.stock) > 0 ? Math.min(100, (consumoAlMax / Number(ins.stock)) * 100) : 0;
                            return (
                              <div key={ins.insumo_id}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5, marginBottom: 3 }}>
                                  <span style={{ color: COL.text, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ins.nombre}</span>
                                  <span style={{ color: COL.muted, whiteSpace: 'nowrap' }}>stock {fmtCant(ins.stock, unidad)}</span>
                                </div>
                                <div style={{ height: 7, borderRadius: 4, background: COL.surface, border: `1px solid ${COL.border}`, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${pct}%`, background: esCuello ? COL.amber : COL.green, borderRadius: 4, transition: 'width 0.3s' }} />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, marginTop: 2, color: COL.faint }}>
                                  <span>req {fmtCant(req, unidad)}/un</span>
                                  <span>{esCuello ? 'limita a ' : 'rinde '}<strong style={{ color: esCuello ? COL.amber : COL.muted }}>{ins.alcanza} un</strong></span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {maxProducciones === 0 && <span style={{ fontSize: 11.5, color: COL.red }}>Stock insuficiente para producir. La ejecución validará de nuevo el stock.</span>}
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: COL.faint }}>No se pudo calcular el stock.</span>
                  )}
                </div>
              )}
            </>
          )}

          {paso === 2 && (
            <>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setModo('unidades')}
                  style={{
                    flex: 1, padding: '9px 10px', borderRadius: 8, fontFamily: 'inherit', fontSize: 12.5,
                    border: modo === 'unidades' ? `2px solid ${COL.pri2}` : `1px solid ${COL.border}`,
                    background: modo === 'unidades' ? COL.blueBg : COL.surface,
                    color: modo === 'unidades' ? COL.pri2 : COL.muted, cursor: 'pointer',
                    fontWeight: modo === 'unidades' ? 700 : 500,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  }}
                >
                  <FiHash size={14} /> Por unidades
                </button>
                <button
                  type="button"
                  onClick={() => setModo('ingrediente')}
                  style={{
                    flex: 1, padding: '9px 10px', borderRadius: 8, fontFamily: 'inherit', fontSize: 12.5,
                    border: modo === 'ingrediente' ? `2px solid ${COL.pri2}` : `1px solid ${COL.border}`,
                    background: modo === 'ingrediente' ? COL.blueBg : COL.surface,
                    color: modo === 'ingrediente' ? COL.pri2 : COL.muted, cursor: 'pointer',
                    fontWeight: modo === 'ingrediente' ? 700 : 500,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  }}
                >
                  <FiBox size={14} /> Por ingrediente
                </button>
              </div>

              {modo === 'unidades' ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <Field label="Cantidad esperada (unidades)" icon={FiHash} required error={errors.cantidad} hint={maxProducciones > 0 ? `Máximo disponible: ${maxProducciones} unidades` : undefined} COL={COL}>
                      <input type="number" inputMode="decimal" step="1" min="0" max={maxProducciones > 0 ? maxProducciones : undefined} value={cantidad} onChange={(e) => setCantidad(e.target.value)} onBlur={ajustarCantidad} style={inputStyle(COL)} />
                    </Field>
                  </div>
                  {maxProducciones > 0 && (
                    <Boton COL={COL} ghost onClick={() => setCantidad(String(maxProducciones))}>Usar máx</Boton>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <Field label="Ingrediente de referencia" icon={FiBox} required error={errors.insumoRef} hint="La cantidad se convierte en unidades de venta según lo que consume la receta por unidad." COL={COL}>
                    <PickerTrigger COL={COL} icon={FiBox} error={!!errors.insumoRef} onClick={() => setPicker('insumo')}>
                      {insumoRefInfo ? (
                        <>
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COL.text }}>{insumoRefInfo.nombre}</span>
                          <span style={{ fontSize: 11, color: COL.faint, whiteSpace: 'nowrap' }}>req {fmtCant(insumoRefInfo.requerido_por_unidad, insumoRefInfo.unidad_simbolo)}/un</span>
                          <FiCheck size={14} style={{ flexShrink: 0, color: COL.green }} />
                        </>
                      ) : (
                        <span style={{ flex: 1, color: COL.muted }}>Seleccionar ingrediente…</span>
                      )}
                    </PickerTrigger>
                  </Field>
                  <Field label="Cantidad a usar del ingrediente" icon={FiHash} required error={errors.cantRef} hint={insumoRefInfo ? `Stock disponible: ${fmtCant(stockRef, insumoRefInfo.unidad_simbolo)}` : 'Ej: 1 kg de harina, 1 L de agua…'} COL={COL}>
                    <input type="number" inputMode="decimal" step="0.01" min="0" max={insumoRefInfo ? stockRef : undefined} value={cantRef} onChange={(e) => setCantRef(e.target.value)} onBlur={ajustarCantRef} placeholder="0.00" style={inputStyle(COL)} />
                  </Field>
                  {insumoRef && Number(cantRef) > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, background: COL.bg2, borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, color: COL.muted }}>
                        <span>Producirá</span>
                        <span><strong style={{ color: COL.text, fontSize: 15 }}>{unidadesEfectivas}</strong> unidades</span>
                      </div>
                      {limitadoPorOtros && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: COL.amber }}>
                          <FiAlertTriangle size={13} /> Otros ingredientes limitan a {maxProducciones} unidades. Se ajustó automáticamente.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <Field label="Notas (opcional)" icon={FiInfo} COL={COL}>
                <input value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Lote, turno, etc." style={inputStyle(COL)} />
              </Field>
            </>
          )}

          {paso === 3 && (
            <>
              <div style={{ background: COL.blueBg, border: `1px solid ${COL.border}`, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: COL.muted }}>Receta</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: COL.text, textAlign: 'right', flex: 1, marginLeft: 12 }}>{recetaSel?.nombre}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: COL.muted }}>Sede</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: COL.text }}>{sedeSel?.nombre || sedeSel?.codigo}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: COL.muted }}>Cantidad</span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: COL.pri2 }}>{unidadesEfectivas} unidades</span>
                </div>
                <div style={{ borderTop: `1px solid ${COL.border}`, margin: '2px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: COL.muted }}>Costo unitario</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: COL.text }}>{costoUnitario != null ? money(costoUnitario) : '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: COL.muted }}>Costo total estimado</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: COL.green }}>{costoTotal != null ? money(costoTotal) : '—'}</span>
                </div>
              </div>

              {capacidad?.insumos?.length > 0 && unidadesEfectivas > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: COL.faint, textTransform: 'uppercase', letterSpacing: 0.5 }}>Consumirá</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {capacidad.insumos.map((ins) => (
                      <div key={ins.insumo_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COL.text }}>{ins.nombre}</span>
                        <span style={{ color: COL.muted, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtCant(unidadesEfectivas * Number(ins.requerido_por_unidad), ins.unidad_simbolo)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {errors.general && <div style={{ padding: '0 18px', color: COL.red, fontSize: 12.5 }}>{errors.general}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: 16, borderTop: `1px solid ${COL.border}`, background: COL.surface }}>
          <Boton COL={COL} ghost onClick={() => (paso === 1 ? onClose() : setPaso(paso - 1))} disabled={guardando}>
            {paso === 1 ? 'Cancelar' : 'Atrás'}
          </Boton>
          {paso < 3 ? (
            <Boton COL={COL} onClick={continuar}>Continuar</Boton>
          ) : (
            <Boton COL={COL} onClick={guardar} disabled={guardando}>{guardando ? 'Creando…' : 'Crear orden'}</Boton>
          )}
        </div>
      </div>

      {picker === 'receta' && (
        <SelectorModal
          title="Seleccionar receta"
          subtitle="¿Qué receta vas a ejecutar?"
          icon={FiLayers}
          COL={COL}
          isMobile={isMobile}
          items={recetas.map((r) => ({ key: r.id, nombre: r.nombre, codigo: r.resultado_nombre ? `Produce: ${r.resultado_nombre}` : null, meta: r.rendimiento > 1 ? `Rinde ${r.rendimiento} un` : null }))}
          value={recetaId}
          onClose={() => setPicker(null)}
          onSelect={(x) => { setRecetaId(String(x.key)); setCantidad(''); setInsumoRef(''); setCantRef(''); setPicker(null); }}
        />
      )}
      {picker === 'sede' && (
        <SelectorModal
          title="Seleccionar sede"
          subtitle="¿En qué sede se ejecuta la producción?"
          icon={FiPackage}
          COL={COL}
          isMobile={isMobile}
          items={sedes.map((s) => ({ key: s.codigo, nombre: s.nombre || s.codigo, codigo: s.codigo, meta: null }))}
          value={sede}
          onClose={() => setPicker(null)}
          onSelect={(x) => { setSede(x.key); setPicker(null); }}
        />
      )}
      {picker === 'insumo' && (
        <SelectorModal
          title="Ingrediente de referencia"
          subtitle="La cantidad a usar se convierte en unidades de venta"
          icon={FiBox}
          COL={COL}
          isMobile={isMobile}
          emptyText="No hay insumos en esta receta."
          items={(capacidad?.insumos || []).map((ins) => ({ key: ins.insumo_id, nombre: ins.nombre, codigo: ins.codigo, meta: `req ${fmtCant(ins.requerido_por_unidad, ins.unidad_simbolo)}/un · stock ${fmtCant(ins.stock, ins.unidad_simbolo)}` }))}
          value={insumoRef}
          onClose={() => setPicker(null)}
          onSelect={(x) => { setInsumoRef(String(x.key)); setCantRef(''); setPicker(null); }}
        />
      )}
    </Overlay>
  );
}

function PickerTrigger({ COL, icon: Icon, error, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', boxSizing: 'border-box',
        padding: '10px 12px', borderRadius: 8, border: `1px solid ${error ? COL.red : COL.border}`,
        background: COL.surface, color: COL.text, fontSize: 13, cursor: 'pointer',
        textAlign: 'left', fontFamily: 'inherit',
      }}
    >
      {Icon && <Icon size={14} style={{ flexShrink: 0, color: COL.faint }} />}
      {children}
      <FiChevronDown size={14} style={{ flexShrink: 0, color: COL.faint }} />
    </button>
  );
}

function SelectorModal({ title, subtitle, icon: Icon, items, value, onClose, onSelect, COL, isMobile, emptyText }) {
  const [q, setQ] = useState('');
  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((x) => (x.nombre || '').toLowerCase().includes(t) || (x.codigo || '').toLowerCase().includes(t));
  }, [q, items]);

  return (
    <Overlay COL={COL} onClose={onClose} isMobile={isMobile}>
      <div style={{ ...S.modal, background: COL.surface, maxWidth: 520 }}>
        <ModalHeader icon={Icon} title={title} subtitle={subtitle} onClose={onClose} COL={COL} />
        <div style={{ padding: '14px 18px' }}>
          <div style={{ position: 'relative' }}>
            <FiSearch size={14} style={{ position: 'absolute', left: 10, top: 11, color: COL.faint }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" style={{ ...inputStyle(COL), paddingLeft: 30 }} />
          </div>
        </div>
        <div style={{ padding: '0 18px 8px', maxHeight: 320, overflowY: 'auto' }}>
          {filtrados.length === 0 ? (
            <div style={{ padding: '16px 0', fontSize: 12.5, color: COL.faint, textAlign: 'center' }}>{emptyText || 'Sin resultados'}</div>
          ) : filtrados.map((x) => {
            const activo = String(x.key) === String(value);
            return (
              <button
                key={x.key}
                type="button"
                onClick={() => onSelect(x)}
                style={{
                  display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '11px 12px',
                  border: activo ? `1px solid ${COL.pri2}` : 'none', borderBottom: `1px solid ${COL.border}`,
                  borderRadius: activo ? 8 : 0, marginBottom: activo ? 4 : 0,
                  background: activo ? COL.blueBg : 'transparent',
                  cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: activo ? 700 : 500, color: activo ? COL.pri2 : COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.nombre}</span>
                    {x.meta && <span style={{ fontSize: 10.5, color: COL.faint, whiteSpace: 'nowrap' }}>{x.meta}</span>}
                  </span>
                  {x.codigo && <span style={{ fontSize: 11.5, color: COL.faint, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{x.codigo}</span>}
                </span>
                {activo && <FiCheck size={15} style={{ flexShrink: 0, color: COL.pri2 }} />}
              </button>
            );
          })}
        </div>
        <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Boton COL={COL} ghost onClick={onClose}>Cancelar</Boton>
        </div>
      </div>
    </Overlay>
  );
}

function InsumoModal({ insumo, categorias, unidades, recetas, COL, onClose, onSaved, isMobile }) {
  const editando = !!insumo;
  const [f, setF] = useState({
    nombre: insumo?.nombre || '',
    codigo: insumo?.codigo || '',
    categoria_id: insumo?.categoria_id || '',
    unidad_medida_id: insumo?.unidad_medida_id || '',
    tipo: insumo?.tipo || 'simple',
    receta_id: insumo?.receta_id || '',
    costo_unitario: insumo?.costo_unitario ?? '',
    stock_minimo: insumo?.stock_minimo ?? '',
    stock_objetivo: insumo?.stock_objetivo ?? '',
    notas: insumo?.notas || '',
  });
  const esCompuesto = f.tipo === 'compuesto';
  const [codigoManual, setCodigoManual] = useState(editando);
  const [errors, setErrors] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [picker, setPicker] = useState(null); // 'categoria' | 'unidad' | 'receta'

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onNombre = (v) => {
    set('nombre', v);
    if (!codigoManual) set('codigo', generarCodigo(v));
  };

  const onCodigo = (v) => {
    setCodigoManual(true);
    set('codigo', v.toUpperCase());
  };

  const categoriaSel = categorias.find((c) => String(c.id) === String(f.categoria_id)) || null;
  const unidadSel = unidades.find((u) => String(u.id) === String(f.unidad_medida_id)) || null;
  const recetaSel = recetas.find((r) => String(r.id) === String(f.receta_id)) || null;

  const guardar = async () => {
    const e = {};
    if (!f.nombre.trim()) e.nombre = 'El nombre es obligatorio';
    if (!f.codigo.trim()) e.codigo = 'El código es obligatorio';
    if (esCompuesto && !f.receta_id) e.receta_id = 'Selecciona una receta';
    if (!esCompuesto && f.costo_unitario !== '' && Number(f.costo_unitario) < 0) e.costo_unitario = 'No puede ser negativo';
    if (f.stock_minimo !== '' && Number(f.stock_minimo) < 0) e.stock_minimo = 'No puede ser negativo';
    if (f.stock_objetivo !== '' && Number(f.stock_objetivo) < 0) e.stock_objetivo = 'No puede ser negativo';
    setErrors(e);
    if (Object.keys(e).length) return;

    setGuardando(true);
    try {
      const payload = {
        nombre: f.nombre.trim(),
        codigo: f.codigo.trim(),
        categoria_id: f.categoria_id ? Number(f.categoria_id) : null,
        unidad_medida_id: f.unidad_medida_id ? Number(f.unidad_medida_id) : null,
        tipo: f.tipo,
        receta_id: esCompuesto && f.receta_id ? Number(f.receta_id) : null,
        costo_unitario: esCompuesto ? 0 : (f.costo_unitario === '' ? 0 : Number(f.costo_unitario)),
        stock_minimo: f.stock_minimo === '' ? 0 : Number(f.stock_minimo),
        stock_objetivo: f.stock_objetivo === '' ? 0 : Number(f.stock_objetivo),
        notas: f.notas.trim() || null,
      };
      if (editando) await API.put(`/produccion/insumos/${insumo.id}`, payload);
      else await API.post('/produccion/insumos', payload);
      onClose();
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (typeof d === 'string' && /codigo/i.test(d)) setErrors({ codigo: d });
      else setErrors({ general: 'No se pudo guardar. Intenta de nuevo.' });
      setGuardando(false);
    }
  };

  return (
    <Overlay COL={COL} onClose={onClose} isMobile={isMobile}>
      <div style={{ ...S.modal, background: COL.surface, maxWidth: 540 }}>
        <ModalHeader icon={FiBox} title={editando ? 'Editar insumo' : 'Nuevo insumo'} subtitle={editando ? 'Actualiza los datos de este insumo' : 'Registra un material o componente que usas para producir'} onClose={onClose} COL={COL} />

        <div style={{ padding: '18px 18px 6px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Section label="Tipo de insumo" COL={COL}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => set('tipo', 'simple')}
                style={{
                  flex: 1, padding: '12px 10px', borderRadius: 10, fontFamily: 'inherit', textAlign: 'left',
                  border: !esCompuesto ? `2px solid ${COL.pri2}` : `1px solid ${COL.border}`,
                  background: !esCompuesto ? COL.blueBg : COL.surface, cursor: 'pointer',
                }}
              >
                <FiBox size={18} style={{ color: !esCompuesto ? COL.pri2 : COL.muted }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: !esCompuesto ? COL.pri2 : COL.text, marginTop: 6 }}>Simple</div>
                <div style={{ fontSize: 11, color: COL.muted, marginTop: 2, lineHeight: 1.4 }}>Material comprado: harina, huevo, servilleta…</div>
              </button>
              <button
                type="button"
                onClick={() => set('tipo', 'compuesto')}
                style={{
                  flex: 1, padding: '12px 10px', borderRadius: 10, fontFamily: 'inherit', textAlign: 'left',
                  border: esCompuesto ? `2px solid ${COL.pri2}` : `1px solid ${COL.border}`,
                  background: esCompuesto ? COL.blueBg : COL.surface, cursor: 'pointer',
                }}
              >
                <FiGitBranch size={18} style={{ color: esCompuesto ? COL.pri2 : COL.muted }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: esCompuesto ? COL.pri2 : COL.text, marginTop: 6 }}>Compuesto</div>
                <div style={{ fontSize: 11, color: COL.muted, marginTop: 2, lineHeight: 1.4 }}>Se produce con una receta; costo automático</div>
              </button>
            </div>
          </Section>

          <Section label="Datos básicos" COL={COL}>
            <Field label="Nombre" icon={FiTag} required error={errors.nombre} COL={COL}>
              <input value={f.nombre} onChange={(e) => onNombre(e.target.value)} placeholder="Ej: Harina de trigo panadera" autoFocus style={inputStyle(COL)} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
              <Field label="Código" icon={FiHash} required error={errors.codigo} hint={!editando ? 'Se sugiere automáticamente; puedes editarlo' : 'No se puede cambiar una vez creado'} COL={COL}>
                <input value={f.codigo} onChange={(e) => onCodigo(e.target.value)} disabled={editando} placeholder="Ej: HAR-PAN" style={{ ...inputStyle(COL), textTransform: 'uppercase', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }} />
              </Field>
              <Field label="Categoría" icon={FiLayers} COL={COL}>
                <PickerTrigger COL={COL} icon={FiLayers} onClick={() => setPicker('categoria')}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: categoriaSel ? COL.text : COL.muted }}>{categoriaSel ? categoriaSel.nombre : 'Sin categoría…'}</span>
                  {categoriaSel && <FiCheck size={14} style={{ flexShrink: 0, color: COL.green }} />}
                </PickerTrigger>
              </Field>
            </div>
            <Field label="Unidad de medida" icon={FiTool} hint={unidadSel ? `${unidadSel.nombre}${unidadSel.simbolo ? ' (' + unidadSel.simbolo + ')' : ''}` : undefined} COL={COL}>
              <PickerTrigger COL={COL} icon={FiTool} onClick={() => setPicker('unidad')}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: unidadSel ? COL.text : COL.muted }}>{unidadSel ? `${unidadSel.nombre}${unidadSel.simbolo ? ' (' + unidadSel.simbolo + ')' : ''}` : 'Sin unidad…'}</span>
                {unidadSel && <FiCheck size={14} style={{ flexShrink: 0, color: COL.green }} />}
              </PickerTrigger>
            </Field>
          </Section>

          <Section label={esCompuesto ? 'Producción' : 'Costos y control'} COL={COL}>
            {esCompuesto ? (
              <Field label="Receta que lo produce" icon={FiLayers} required error={errors.receta_id} hint="El costo se calcula automáticamente a partir de esta receta" COL={COL}>
                <PickerTrigger COL={COL} icon={FiLayers} error={!!errors.receta_id} onClick={() => setPicker('receta')}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: recetaSel ? COL.text : COL.muted }}>{recetaSel ? recetaSel.nombre : 'Seleccionar receta…'}</span>
                  {recetaSel && <FiCheck size={14} style={{ flexShrink: 0, color: COL.green }} />}
                </PickerTrigger>
              </Field>
            ) : (
              <Field label="Costo unitario" icon={FiDollarSign} error={errors.costo_unitario} hint="Costo por unidad para calcular la receta" COL={COL}>
                <div style={{ position: 'relative', width: '100%' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 700, color: COL.muted, pointerEvents: 'none' }}>$</span>
                  <input type="number" inputMode="decimal" step="0.01" min="0" value={f.costo_unitario} onChange={(e) => set('costo_unitario', e.target.value)} placeholder="0.00" style={{ ...inputStyle(COL), paddingLeft: 26 }} />
                </div>
              </Field>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
              <Field label="Stock mínimo" icon={FiAlertTriangle} error={errors.stock_minimo} hint="Alerta cuando el stock esté por debajo" COL={COL}>
                <input type="number" inputMode="decimal" step="0.01" min="0" value={f.stock_minimo} onChange={(e) => set('stock_minimo', e.target.value)} placeholder="0" style={inputStyle(COL)} />
              </Field>
              <Field label="Stock objetivo" icon={FiBarChart2} error={errors.stock_objetivo} hint="Nivel ideal a reponer" COL={COL}>
                <input type="number" inputMode="decimal" step="0.01" min="0" value={f.stock_objetivo} onChange={(e) => set('stock_objetivo', e.target.value)} placeholder="0" style={inputStyle(COL)} />
              </Field>
            </div>
            <Field label="Notas" icon={FiInfo} COL={COL}>
              <textarea value={f.notas} onChange={(e) => set('notas', e.target.value)} rows={2} placeholder="Opcional" style={{ ...inputStyle(COL), resize: 'vertical' }} />
            </Field>
          </Section>
        </div>

        {errors.general && <div style={{ padding: '0 18px', color: COL.red, fontSize: 12.5 }}>{errors.general}</div>}

        <ModalFooter onClose={onClose} COL={COL} onSave={guardar} guardando={guardando} label={editando ? 'Guardar cambios' : 'Crear insumo'} />
      </div>

      {picker === 'categoria' && (
        <SelectorModal
          title="Seleccionar categoría"
          subtitle="¿A qué grupo pertenece este insumo?"
          icon={FiLayers}
          COL={COL}
          isMobile={isMobile}
          items={categorias.map((c) => ({ key: c.id, nombre: c.nombre, codigo: null, meta: null }))}
          value={f.categoria_id}
          onClose={() => setPicker(null)}
          onSelect={(x) => { set('categoria_id', x.key); setPicker(null); }}
        />
      )}
      {picker === 'unidad' && (
        <SelectorModal
          title="Unidad de medida"
          subtitle="kg, L, unidad, minuto…"
          icon={FiTool}
          COL={COL}
          isMobile={isMobile}
          items={unidades.map((u) => ({ key: u.id, nombre: u.nombre, codigo: u.simbolo || null, meta: null }))}
          value={f.unidad_medida_id}
          onClose={() => setPicker(null)}
          onSelect={(x) => { set('unidad_medida_id', x.key); setPicker(null); }}
        />
      )}
      {picker === 'receta' && (
        <SelectorModal
          title="Receta que lo produce"
          subtitle="El costo se calcula automáticamente desde esta receta"
          icon={FiLayers}
          COL={COL}
          isMobile={isMobile}
          emptyText="No hay recetas activas. Crea una receta primero."
          items={recetas.map((r) => ({ key: r.id, nombre: r.nombre, codigo: null, meta: r.costo_estimado != null ? money(r.costo_estimado) + '/un' : null }))}
          value={f.receta_id}
          onClose={() => setPicker(null)}
          onSelect={(x) => { set('receta_id', x.key); setPicker(null); }}
        />
      )}
    </Overlay>
  );
}

function MoverModal({ insumos, categorias, sede, COL, onClose, onSaved, isMobile }) {
  const [tipo, setTipo] = useState('compra');
  const [motivo, setMotivo] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [notas, setNotas] = useState('');
  const [items, setItems] = useState([{ insumo_id: '', cantidad: '' }]);
  const [errors, setErrors] = useState({});
  const [guardando, setGuardando] = useState(false);

  const esSalida = tipo === 'consumo' || tipo === 'merma';
  const setItem = (idx, k, val) => setItems((p) => p.map((it, i) => (i === idx ? { ...it, [k]: val } : it)));
  const signo = esSalida ? '−' : '+';

  const guardar = async () => {
    const e = {};
    const its = items.filter((x) => x.insumo_id && x.cantidad !== '').map((x) => ({ insumo_id: Number(x.insumo_id), cantidad: Number(x.cantidad) }));
    if (!its.length) e.items = 'Añade al menos un insumo';
    if (its.some((x) => !x.cantidad)) e.items = 'Indica la cantidad de cada insumo';
    if (esSalida && !motivo.trim()) e.motivo = 'El motivo es obligatorio';
    if (esSalida && !categoriaId) e.categoriaId = 'Selecciona la categoría de gasto';
    setErrors(e);
    if (Object.keys(e).length) return;

    setGuardando(true);
    try {
      await API.post('/produccion/insumos/movimientos', {
        sede, tipo, motivo: motivo.trim() || null,
        categoria_gasto_id: categoriaId ? Number(categoriaId) : null,
        notas: notas.trim() || null, items: its,
      });
      onClose();
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErrors({ general: typeof d === 'string' ? d : 'No se pudo registrar el movimiento.' });
      setGuardando(false);
    }
  };

  return (
    <Overlay COL={COL} onClose={onClose} isMobile={isMobile}>
      <div style={{ ...S.modal, background: COL.surface, maxWidth: 520 }}>
        <ModalHeader icon={FiRefreshCw} title="Mover stock de insumos" subtitle="Registra una entrada, un consumo o un ajuste de materiales" onClose={onClose} COL={COL} />

        <div style={{ padding: '18px 18px 6px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Tipo de movimiento" icon={FiGitBranch} COL={COL}>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={selectStyle(COL)}>
              <option value="compra">Compra  ·  + suma stock</option>
              <option value="devolucion">Devolución  ·  + suma stock</option>
              <option value="merma">Merma  ·  − resta stock</option>
              <option value="ajuste">Ajuste  ·  ± corrige stock</option>
            </select>
          </Field>

          {esSalida && (
          <Section label="Detalle del movimiento" COL={COL}>
              <Field label="Motivo" icon={FiInfo} required error={errors.motivo} hint="Ej: uso interno, daño, vencimiento, pérdida…" COL={COL}>
                <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="¿Por qué sale este insumo?" list="motivos-comunes" style={inputStyle(COL)} />
                <datalist id="motivos-comunes">
                  <option value="Uso interno" />
                  <option value="Daño" />
                  <option value="Vencimiento" />
                  <option value="Pérdida" />
                  <option value="Devolución a proveedor" />
                  <option value="Otro" />
                </datalist>
              </Field>
              <Field label="Categoría de gasto" icon={FiLayers} required error={errors.categoriaId} COL={COL}>
                <select value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)} style={selectStyle(COL)}>
                  <option value="">Seleccionar…</option>
                  {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </Field>
            </Section>
          )}

          <Section label="Insumos" COL={COL}>
            {items.map((it, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ flex: 2 }}>
                  <select value={it.insumo_id} onChange={(e) => setItem(i, 'insumo_id', e.target.value)} style={selectStyle(COL)}>
                    <option value="">Seleccionar insumo…</option>
                    {insumos.map((x) => <option key={x.id} value={x.id}>{x.nombre} ({x.codigo})</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 10, top: 9, fontSize: 15, fontWeight: 700, color: esSalida ? COL.red : COL.green }}>{signo}</span>
                    <input type="number" inputMode="decimal" step="0.01" min="0" value={it.cantidad} onChange={(e) => setItem(i, 'cantidad', e.target.value)} placeholder="Cant." style={{ ...inputStyle(COL), paddingLeft: 28 }} />
                  </div>
                </div>
                {items.length > 1 && (
                  <div style={{ paddingTop: 8 }}>
                    <IconBtn COL={COL} onClick={() => setItems((p) => p.filter((_, j) => j !== i))}><FiX size={14} /></IconBtn>
                  </div>
                )}
              </div>
            ))}
            {errors.items && <div style={{ color: COL.red, fontSize: 12.5 }}>{errors.items}</div>}
            <Boton COL={COL} ghost onClick={() => setItems((p) => [...p, { insumo_id: '', cantidad: '' }])}><FiPlus size={14} /> Añadir otro insumo</Boton>
          </Section>

          <Field label="Notas (opcional)" icon={FiInfo} COL={COL}>
            <input value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Referencia, factura, etc." style={inputStyle(COL)} />
          </Field>
        </div>

        {errors.general && <div style={{ padding: '0 18px', color: COL.red, fontSize: 12.5 }}>{errors.general}</div>}

        <ModalFooter onClose={onClose} COL={COL} onSave={guardar} guardando={guardando} label="Registrar movimiento" />
      </div>
    </Overlay>
  );
}

function GastoModal({ sede, categorias, COL, onClose, onSaved, isMobile }) {
  const [insumos, setInsumos] = useState([]);
  const [cargandoInsumos, setCargandoInsumos] = useState(true);
  const [q, setQ] = useState('');
  const [insumo, setInsumo] = useState(null);
  const [tipo, setTipo] = useState('consumo');
  const [motivo, setMotivo] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [nota, setNota] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [errors, setErrors] = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!sede) return;
    let activo = true;
    API.get(`/produccion/insumos/stock?sede=${encodeURIComponent(sede)}`)
      .then((r) => { if (activo) setInsumos(r.data?.data || []); })
      .catch(() => {})
      .finally(() => { if (activo) setCargandoInsumos(false); });
    return () => { activo = false; };
  }, [sede]);

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return insumos;
    return insumos.filter((x) => (x.nombre || '').toLowerCase().includes(t) || (x.codigo || '').toLowerCase().includes(t));
  }, [q, insumos]);

  const costoUnitario = insumo ? Number(insumo.costo_unitario || 0) : 0;
  const total = (Number(cantidad) || 0) * costoUnitario;
  const superaStock = insumo && Number(cantidad) > Number(insumo.stock || 0);

  const guardar = async () => {
    const e = {};
    if (!insumo) e.insumo = 'Selecciona un insumo';
    if (!Number(cantidad) || Number(cantidad) <= 0) e.cantidad = 'Indica una cantidad mayor a 0';
    if (!motivo.trim()) e.motivo = 'El motivo es obligatorio';
    if (!categoriaId) e.categoriaId = 'Selecciona la categoría de gasto';
    setErrors(e);
    if (Object.keys(e).length) return;

    setGuardando(true);
    try {
      await API.post('/produccion/insumos/movimientos', {
        sede,
        tipo,
        motivo: motivo.trim() || null,
        categoria_gasto_id: Number(categoriaId),
        notas: nota.trim() || null,
        items: [{ insumo_id: insumo.id, cantidad: Number(cantidad) }],
      });
      onClose();
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErrors({ general: typeof d === 'string' ? d : 'No se pudo registrar el movimiento.' });
      setGuardando(false);
    }
  };

  return (
    <Overlay COL={COL} onClose={onClose} isMobile={isMobile}>
      <div style={{ ...S.modal, background: COL.surface, maxWidth: 520 }}>
        <ModalHeader icon={FiDollarSign} title="Nuevo movimiento" subtitle="Descuenta un insumo (simple o compuesto) y registra la nota" onClose={onClose} COL={COL} />

        <div style={{ padding: '18px 18px 6px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Insumo" icon={FiBox} required error={errors.insumo} hint={insumo ? `Stock disponible: ${num(insumo.stock)} · Costo ${money(costoUnitario)}/un` : undefined} COL={COL}>
            {!insumo ? (
              <div style={{ position: 'relative' }}>
                <FiSearch size={14} style={{ position: 'absolute', left: 10, top: 11, color: COL.faint }} />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o código…" style={{ ...inputStyle(COL), paddingLeft: 30 }} />
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '9px 10px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.inputBg }}>
                <span style={{ fontSize: 13, color: COL.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{insumo.nombre} <span style={{ color: COL.faint, fontWeight: 400 }}>({insumo.codigo})</span></span>
                <button type="button" onClick={() => { setInsumo(null); setQ(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.faint, padding: 2, flexShrink: 0 }}>
                  <FiX size={14} />
                </button>
              </div>
            )}
          </Field>

          {!insumo && (
            <div style={{ maxHeight: 240, overflowY: 'auto', border: `1px solid ${COL.border}`, borderRadius: 10 }}>
              {cargandoInsumos ? (
                <div style={{ padding: '16px', fontSize: 12.5, color: COL.faint, textAlign: 'center' }}>Cargando insumos…</div>
              ) : filtrados.length === 0 ? (
                <div style={{ padding: '16px', fontSize: 12.5, color: COL.faint, textAlign: 'center' }}>Sin resultados</div>
              ) : filtrados.map((x) => (
                <button key={x.id} type="button" onClick={() => { setInsumo(x); setQ(''); }} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '10px 12px', border: 'none', borderBottom: `1px solid ${COL.border}`, background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, color: COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.nombre}</span>
                    <span style={{ display: 'block', fontSize: 11, color: COL.faint, marginTop: 1 }}>
                      {x.codigo} · stock {num(x.stock)} · {money(x.costo_unitario)}/un
                    </span>
                  </span>
                  {x.tipo === 'compuesto' && <span style={{ ...S.badge, background: COL.blueBg, color: COL.infoText, fontSize: 10, padding: '2px 6px' }}>compuesto</span>}
                </button>
              ))}
            </div>
          )}

          <Section label="Detalle del movimiento" COL={COL}>
            <Field label="Tipo" icon={FiGitBranch} COL={COL}>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={selectStyle(COL)}>
                <option value="consumo">Consumo · − resta stock</option>
                <option value="merma">Merma · − resta stock</option>
              </select>
            </Field>
            <Field label="Motivo" icon={FiInfo} required error={errors.motivo} hint="Ej: uso interno, daño, vencimiento, pérdida…" COL={COL}>
              <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="¿Por qué sale este insumo?" list="gasto-motivos" style={inputStyle(COL)} />
              <datalist id="gasto-motivos">
                <option value="Uso interno" />
                <option value="Daño" />
                <option value="Vencimiento" />
                <option value="Pérdida" />
                <option value="Devolución a proveedor" />
                <option value="Otro" />
              </datalist>
            </Field>
            <Field label="Categoría de gasto" icon={FiLayers} required error={errors.categoriaId} COL={COL}>
              <select value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)} style={selectStyle(COL)}>
                <option value="">Seleccionar…</option>
                {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </Field>
            <Field label="Cantidad" icon={FiHash} required error={errors.cantidad} COL={COL}>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={cantidad} onChange={(e) => setCantidad(e.target.value)} placeholder="0.00" style={inputStyle(COL)} />
            </Field>
            {superaStock && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: COL.amber }}>
                <FiAlertTriangle size={13} /> La cantidad supera el stock disponible ({num(insumo.stock)}).
              </div>
            )}
            <Field label="Nota (opcional)" icon={FiInfo} COL={COL}>
              <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Detalle o referencia…" style={inputStyle(COL)} />
            </Field>
          </Section>

          {insumo && Number(cantidad) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: COL.bg2, borderRadius: 10, padding: '12px 14px' }}>
              <span style={{ fontSize: 13, color: COL.muted }}>Costo total estimado</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: COL.red }}>{money(total)}</span>
            </div>
          )}
        </div>

        {errors.general && <div style={{ padding: '0 18px', color: COL.red, fontSize: 12.5 }}>{errors.general}</div>}

        <ModalFooter onClose={onClose} COL={COL} onSave={guardar} guardando={guardando} label="Registrar movimiento" />
      </div>
    </Overlay>
  );
}

function RecetaModal({ receta, insumos, productos, COL, onClose, onRefresh, isMobile }) {
  const [f, setF] = useState({
    id: receta?.id,
    nombre: receta?.nombre || '',
    rendimiento: receta?.rendimiento != null ? receta.rendimiento : 1,
    notas: receta?.notas || '',
  });
  const [resultadoTipo, setResultadoTipo] = useState(receta?.resultado_tipo || '');
  const [insumoResultadoId, setInsumoResultadoId] = useState(receta?.insumo_resultado_id || '');
  const [productoResultadoCodigo, setProductoResultadoCodigo] = useState(receta?.producto_resultado_codigo || '');
  const [items, setItems] = useState((receta?.insumos || []).map((i) => ({ insumo_id: i.insumo_id, cantidad: i.cantidad, merma: i.merma || '' })));
  const [buscadorInsumo, setBuscadorInsumo] = useState(null);
  const [errors, setErrors] = useState({});
  const [guardando, setGuardando] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setItem = (idx, k, val) => setItems((p) => p.map((it, i) => (i === idx ? { ...it, [k]: val } : it)));

  const insumosElegibles = useMemo(
    () => insumos.filter((x) => x.receta_id == null || (f.id != null && Number(x.receta_id) === Number(f.id))),
    [insumos, f.id]
  );

  const costoTotal = useMemo(() => {
    let total = 0;
    items.forEach((it) => {
      const ins = insumos.find((x) => x.id === Number(it.insumo_id));
      if (!ins) return;
      const cant = Number(it.cantidad) || 0;
      const merma = Number(it.merma) || 0;
      total += cant * (1 + merma / 100) * (Number(ins.costo_unitario) || 0);
    });
    return total;
  }, [items, insumos]);

  const rendimiento = Number(f.rendimiento) > 0 ? Number(f.rendimiento) : 1;
  const costoUnitario = costoTotal / rendimiento;

  const guardar = async () => {
    const e = {};
    if (!f.nombre.trim()) e.nombre = 'El nombre es obligatorio';
    const validItems = items.filter((x) => x.insumo_id && Number(x.cantidad) > 0);
    if (!validItems.length) e.items = 'Añade al menos un insumo';
    if (!resultadoTipo) e.resultado = 'Asigna el producto resultante de esta receta';
    else if (resultadoTipo === 'insumo' && !insumoResultadoId) e.resultado = 'Selecciona el insumo compuesto resultante';
    else if (resultadoTipo === 'producto' && !productoResultadoCodigo) e.resultado = 'Selecciona el producto compuesto resultante';
    setErrors(e);
    if (Object.keys(e).length) return;

    setGuardando(true);
    try {
      const payload = {
        nombre: f.nombre.trim(),
        notas: f.notas.trim() || null,
        rendimiento: rendimiento,
        resultado_tipo: resultadoTipo,
        insumo_resultado_id: resultadoTipo === 'insumo' ? Number(insumoResultadoId) : null,
        producto_resultado_codigo: resultadoTipo === 'producto' ? productoResultadoCodigo : null,
        insumos: validItems.map((x) => ({ insumo_id: Number(x.insumo_id), cantidad: Number(x.cantidad), merma: Number(x.merma) || 0 })),
      };
      if (f.id) await API.put(`/produccion/recetas/${f.id}`, payload);
      else await API.post('/produccion/recetas', payload);
      onClose();
      onRefresh();
    } catch (err) {
      setErrors({ general: err?.response?.data?.detail || 'No se pudo guardar la receta.' });
      setGuardando(false);
    }
  };

  return (
    <>
      <Overlay COL={COL} onClose={onClose} isMobile={isMobile}>
        <div style={{ ...S.modal, background: COL.surface, maxWidth: 600 }}>
          <ModalHeader icon={FiLayers} title={receta ? 'Editar receta' : 'Nueva receta'} subtitle="Define qué insumos componen esta receta" onClose={onClose} COL={COL} />

        <div style={{ padding: '18px 18px 6px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Nombre de la receta" icon={FiTag} required error={errors.nombre} COL={COL}>
            <input value={f.nombre} onChange={(e) => set('nombre', e.target.value)} placeholder="Ej: Producto principal" autoFocus style={inputStyle(COL)} />
          </Field>

          <Field
            label="Rendimiento (unidades de venta por receta)"
            icon={FiHash}
            error={errors.rendimiento}
            hint="¿Cuántas unidades se venden de una tanda de esta receta? Ej: una torta picada en 8 porciones = 8. Los panes se venden por unidad = 1."
            COL={COL}
          >
            <input type="number" inputMode="decimal" step="1" min="1" value={f.rendimiento} onChange={(e) => set('rendimiento', e.target.value)} style={inputStyle(COL)} />
          </Field>

          <Section
            label="Insumos de la receta"
            right={<span style={{ fontSize: 12.5, color: COL.muted, fontWeight: 700 }}>Costo receta {money(costoTotal)} · <span style={{ color: COL.green }}>{money(costoUnitario)}/un</span></span>}
            COL={COL}
          >
            {items.map((it, i) => {
              const insumoActivo = insumos.find((x) => x.id === Number(it.insumo_id));
              const unidad = insumoActivo?.unidad_simbolo || insumoActivo?.unidad_nombre || '';
              const costoLinea = (Number(it.cantidad) || 0) * (1 + (Number(it.merma) || 0) / 100) * (Number(insumoActivo?.costo_unitario) || 0);
              return (
                <div key={i} style={{ background: COL.bg2, borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr auto' : '1fr auto', gap: 8, alignItems: 'center' }}>
                    <button type="button" onClick={() => setBuscadorInsumo(i)} style={{ width: '100%', boxSizing: 'border-box', padding: '9px 10px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.surface, color: insumoActivo ? COL.text : COL.muted, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, textAlign: 'left', minWidth: 0, fontFamily: 'inherit' }}>
                      <FiSearch size={13} style={{ flexShrink: 0, color: COL.faint }} />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{insumoActivo ? `${insumoActivo.nombre} (${insumoActivo.codigo})` : 'Buscar insumo…'}</span>
                    </button>
                    {items.length > 1 && (
                      <IconBtn COL={COL} onClick={() => setItems((p) => p.filter((_, j) => j !== i))}><FiX size={14} /></IconBtn>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 8, alignItems: 'center' }}>
                    <div style={{ position: 'relative' }}>
                      <input type="number" inputMode="decimal" step="0.01" min="0" value={it.cantidad} onChange={(e) => setItem(i, 'cantidad', e.target.value)} placeholder="Cant." style={{ ...inputStyle(COL), paddingRight: unidad ? 34 : 10 }} />
                      {unidad && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: COL.faint, pointerEvents: 'none' }}>{unidad}</span>}
                    </div>
                    <div style={{ position: 'relative' }}>
                      <input type="number" inputMode="decimal" step="0.1" min="0" value={it.merma} onChange={(e) => setItem(i, 'merma', e.target.value)} placeholder="Merma %" style={{ ...inputStyle(COL), paddingRight: 26 }} />
                      <span style={{ position: 'absolute', right: 10, top: 9, fontSize: 12, color: COL.faint }}>%</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      {costoLinea > 0 && <span style={{ fontSize: 12, color: COL.muted, fontWeight: 700 }}>{money(costoLinea)}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
            {errors.items && <div style={{ color: COL.red, fontSize: 12.5 }}>{errors.items}</div>}
            <Boton COL={COL} ghost onClick={() => setItems((p) => [...p, { insumo_id: '', cantidad: '', merma: '' }])}><FiPlus size={14} /> Añadir insumo</Boton>
          </Section>

          <Section label="Producto resultante" COL={COL}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => { setResultadoTipo('insumo'); setProductoResultadoCodigo(''); }}
                style={{
                  flex: '1 1 200px', padding: '10px 14px', borderRadius: 10, fontFamily: 'inherit',
                  border: resultadoTipo === 'insumo' ? `2px solid ${COL.pri2}` : `1px solid ${COL.border}`,
                  background: resultadoTipo === 'insumo' ? COL.blueBg : COL.surface,
                  color: resultadoTipo === 'insumo' ? COL.pri2 : COL.muted, cursor: 'pointer',
                  fontSize: 13, fontWeight: resultadoTipo === 'insumo' ? 700 : 500,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                }}
              >
                <FiBox size={16} />
                <span>Insumo compuesto</span>
                <span style={{ fontSize: 10.5, fontWeight: 400, opacity: 0.8 }}>Subproducto intermedio</span>
              </button>
              <button
                type="button"
                onClick={() => { setResultadoTipo('producto'); setInsumoResultadoId(''); }}
                style={{
                  flex: '1 1 200px', padding: '10px 14px', borderRadius: 10, fontFamily: 'inherit',
                  border: resultadoTipo === 'producto' ? `2px solid ${COL.pri2}` : `1px solid ${COL.border}`,
                  background: resultadoTipo === 'producto' ? COL.blueBg : COL.surface,
                  color: resultadoTipo === 'producto' ? COL.pri2 : COL.muted, cursor: 'pointer',
                  fontSize: 13, fontWeight: resultadoTipo === 'producto' ? 700 : 500,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                }}
              >
                <FiPackage size={16} />
                <span>Producto compuesto</span>
                <span style={{ fontSize: 10.5, fontWeight: 400, opacity: 0.8 }}>Artículo del catálogo</span>
              </button>
            </div>

            {resultadoTipo === 'insumo' && (
              <ResultadoPicker
                COL={COL}
                icon={FiBox}
                label="Insumo resultante"
                placeholder="Buscar insumo por nombre o código…"
                emptyText="No hay insumos disponibles. Crea insumos simples en la pestaña Insumos."
                items={insumosElegibles.map((x) => ({
                  key: String(x.id),
                  nombre: x.nombre,
                  codigo: x.codigo,
                  extra: x.tipo === 'compuesto' ? 'Compuesto' : null,
                  disabled: false,
                }))}
                value={insumoResultadoId ? String(insumoResultadoId) : ''}
                onChange={(v) => setInsumoResultadoId(v)}
                error={errors.resultado}
                hint="El insumo quedará marcado como compuesto y su costo se calculará desde esta receta."
              />
            )}

            {resultadoTipo === 'producto' && (
              <ResultadoPicker
                COL={COL}
                icon={FiPackage}
                label="Producto resultante"
                placeholder="Buscar producto por nombre o código…"
                emptyText="No hay productos disponibles. Crea el producto en el inventario primero."
                items={productos.map((p) => {
                  const ocupado = p.receta_id != null && (f.id == null || Number(p.receta_id) !== Number(f.id));
                  return {
                    key: p.codigo,
                    nombre: p.nombre,
                    codigo: p.codigo,
                    extra: ocupado ? `Vinculado a: ${p.receta_nombre || 'otra receta'}` : null,
                    disabled: ocupado,
                  };
                })}
                value={productoResultadoCodigo}
                onChange={setProductoResultadoCodigo}
                error={errors.resultado}
                hint="Un producto compuesto solo puede estar vinculado a una receta."
              />
            )}

            {!resultadoTipo && errors.resultado && (
              <div style={{ color: COL.red, fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 4 }}>
                <FiAlertTriangle size={12} /> {errors.resultado}
              </div>
            )}
          </Section>

          <Field label="Notas (opcional)" icon={FiInfo} COL={COL}>
            <input value={f.notas} onChange={(e) => set('notas', e.target.value)} placeholder="Preparación, tips, etc." style={inputStyle(COL)} />
          </Field>

          {items.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, background: COL.blueBg, border: `1px solid ${COL.border}`, borderRadius: 12, padding: 12 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: COL.faint, textTransform: 'uppercase', letterSpacing: 0.4 }}>Insumos</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: COL.text }}>{items.filter((x) => x.insumo_id && Number(x.cantidad) > 0).length}</div>
              </div>
              <div style={{ textAlign: 'center', borderLeft: `1px solid ${COL.border}`, borderRight: `1px solid ${COL.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: COL.faint, textTransform: 'uppercase', letterSpacing: 0.4 }}>Costo receta</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: COL.text }}>{money(costoTotal)}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: COL.faint, textTransform: 'uppercase', letterSpacing: 0.4 }}>Costo / unidad</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: COL.green }}>{money(costoUnitario)}</div>
              </div>
            </div>
          )}
        </div>

        {errors.general && <div style={{ padding: '0 18px', color: COL.red, fontSize: 12.5 }}>{errors.general}</div>}

        <ModalFooter onClose={onClose} COL={COL} onSave={guardar} guardando={guardando} label={receta ? 'Guardar cambios' : 'Crear receta'} />
      </div>
    </Overlay>

    {buscadorInsumo != null && (
      <BuscadorInsumoModal
        insumos={insumos}
        COL={COL}
        isMobile={isMobile}
        onClose={() => setBuscadorInsumo(null)}
        onSelect={(x) => { setItem(buscadorInsumo, 'insumo_id', x.id); setBuscadorInsumo(null); }}
      />
    )}
    </>
  );
}

function ResultadoPicker({ COL, icon: Icon, label, placeholder, emptyText, items, value, onChange, error, hint }) {
  const [q, setQ] = useState('');
  const seleccionado = items.find((x) => x.key === value) || null;
  const [abierto, setAbierto] = useState(false);

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((x) => (x.nombre || '').toLowerCase().includes(t) || (x.codigo || '').toLowerCase().includes(t));
  }, [q, items]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
        {Icon && <Icon size={13} style={{ color: COL.muted, flexShrink: 0 }} />}
        <span style={{ fontSize: 12, fontWeight: 700, color: COL.muted, whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ color: COL.red, fontSize: 12 }}>*</span>
      </div>

      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%', boxSizing: 'border-box',
          padding: '9px 11px', borderRadius: 8, border: `1px solid ${error ? COL.red : COL.border}`,
          background: COL.surface, color: seleccionado ? COL.text : COL.muted, fontSize: 13,
          cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
        }}
      >
        <FiSearch size={14} style={{ flexShrink: 0, color: COL.faint }} />
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {seleccionado ? `${seleccionado.nombre} (${seleccionado.codigo})` : placeholder}
        </span>
        {seleccionado && <FiCheck size={14} style={{ flexShrink: 0, color: COL.green }} />}
        <FiChevronDown size={14} style={{ flexShrink: 0, color: COL.faint, transform: abierto ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {abierto && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ position: 'relative' }}>
            <FiSearch size={13} style={{ position: 'absolute', left: 10, top: 10, color: COL.faint }} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              style={{ ...inputStyle(COL), paddingLeft: 30 }}
            />
          </div>
          <div style={{ border: `1px solid ${COL.border}`, borderRadius: 10, maxHeight: 220, overflowY: 'auto', background: COL.surface }}>
            {filtrados.length === 0 ? (
              <div style={{ padding: '14px', fontSize: 12.5, color: COL.faint, textAlign: 'center' }}>{emptyText || 'Sin resultados'}</div>
            ) : filtrados.map((x) => {
              const activo = x.key === value;
              return (
                <button
                  key={x.key}
                  type="button"
                  disabled={x.disabled}
                  onClick={() => { onChange(x.key); setAbierto(false); setQ(''); }}
                  style={{
                    display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '10px 12px',
                    border: 'none', borderBottom: `1px solid ${COL.border}`,
                    background: activo ? COL.blueBg : 'transparent',
                    cursor: x.disabled ? 'not-allowed' : 'pointer', textAlign: 'left', fontFamily: 'inherit',
                    opacity: x.disabled ? 0.5 : 1,
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: activo ? 700 : 500, color: activo ? COL.pri2 : COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.nombre}</span>
                      {x.extra && <Badge COL={COL} tone={x.disabled ? 'red' : 'blue'}>{x.extra}</Badge>}
                    </span>
                    <span style={{ fontSize: 11.5, color: COL.faint, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{x.codigo}</span>
                  </span>
                  {activo && <FiCheck size={15} style={{ flexShrink: 0, color: COL.pri2 }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error ? (
        <span style={{ fontSize: 11.5, color: COL.red, display: 'flex', alignItems: 'center', gap: 4 }}>
          <FiAlertTriangle size={12} /> {error}
        </span>
      ) : hint ? (
        <span style={{ fontSize: 11, color: COL.faint, lineHeight: 1.4 }}>{hint}</span>
      ) : null}
    </div>
  );
}

function Badge({ COL, tone = 'neutral', children }) {
  const tones = {
    neutral: { bg: COL.bg2, color: COL.muted },
    green: { bg: COL.greenBg, color: COL.green },
    red: { bg: COL.redBg, color: COL.red },
    blue: { bg: COL.blueBg, color: COL.pri2 },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 5, background: t.bg, color: t.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
      {children}
    </span>
  );
}

function BuscadorInsumoModal({ insumos, COL, onClose, onSelect, isMobile }) {
  const [q, setQ] = useState('');
  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return insumos;
    return insumos.filter((x) => (x.nombre || '').toLowerCase().includes(t) || (x.codigo || '').toLowerCase().includes(t));
  }, [q, insumos]);

  return (
    <Overlay COL={COL} onClose={onClose} isMobile={isMobile}>
      <div style={{ ...S.modal, background: COL.surface, maxWidth: 520 }}>
        <ModalHeader icon={FiBox} title="Buscar insumo" subtitle="Selecciona un insumo para la receta" onClose={onClose} COL={COL} />
        <div style={{ padding: '14px 18px' }}>
          <div style={{ position: 'relative' }}>
            <FiSearch size={14} style={{ position: 'absolute', left: 10, top: 11, color: COL.faint }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o código…" style={{ ...inputStyle(COL), paddingLeft: 30 }} />
          </div>
        </div>
        <div style={{ padding: '0 18px 6px', maxHeight: 320, overflowY: 'auto' }}>
          {filtrados.length === 0 ? (
            <div style={{ padding: '14px 0', fontSize: 12.5, color: COL.faint }}>Sin resultados</div>
          ) : filtrados.map((x) => (
            <button key={x.id} type="button" onClick={() => onSelect(x)} style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '11px 12px', border: 'none', borderBottom: `1px solid ${COL.border}`, background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
              <span style={{ fontSize: 13, color: COL.text }}>{x.nombre}</span>
              <span style={{ fontSize: 11.5, color: COL.faint, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{x.codigo}</span>
            </button>
          ))}
        </div>
        <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Boton COL={COL} ghost onClick={onClose}>Cancelar</Boton>
        </div>
      </div>
    </Overlay>
  );
}

function CatsModal({ categorias, unidades, COL, onClose, onRefresh, isMobile }) {
  const [newCat, setNewCat] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [newUnitSym, setNewUnitSym] = useState('');
  const [guardando, setGuardando] = useState(false);

  const addCat = async () => {
    if (!newCat.trim()) return;
    setGuardando(true);
    try { await API.post('/produccion/categorias', { nombre: newCat.trim(), aplica_a: 'insumo' }); setNewCat(''); onRefresh(); } catch { }
    setGuardando(false);
  };
  const addUnit = async () => {
    if (!newUnit.trim()) return;
    setGuardando(true);
    try { await API.post('/produccion/unidades', { nombre: newUnit.trim(), simbolo: newUnitSym.trim() || null }); setNewUnit(''); setNewUnitSym(''); onRefresh(); } catch { }
    setGuardando(false);
  };

  const eliminarCat = async (c) => {
    if (!window.confirm(`¿Eliminar la categoría "${c.nombre}"? Los insumos que la usan quedarán sin categoría.`)) return;
    try { await API.delete(`/produccion/categorias/${c.id}`); onRefresh(); } catch { }
  };
  const eliminarUnit = async (u) => {
    if (!window.confirm(`¿Eliminar la unidad "${u.nombre}"? Los insumos que la usan quedarán sin unidad.`)) return;
    try { await API.delete(`/produccion/unidades/${u.id}`); onRefresh(); } catch { }
  };

  return (
    <Overlay COL={COL} onClose={onClose} isMobile={isMobile}>
      <div style={{ ...S.modal, background: COL.surface, maxWidth: 520 }}>
        <ModalHeader icon={FiLayers} title="Categorías y unidades" subtitle="Personaliza la clasificación de tus insumos" onClose={onClose} COL={COL} />

        <div style={{ padding: '18px 18px 6px', display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: COL.text, marginBottom: 4 }}>Categorías de insumos</div>
            <div style={{ fontSize: 11.5, color: COL.faint, marginBottom: 10 }}>Agrupa tus insumos (materia prima, componentes, materiales…)</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCat()} placeholder="Nueva categoría" style={inputStyle(COL)} />
              <Boton COL={COL} onClick={addCat} disabled={guardando || !newCat.trim()}>Añadir</Boton>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, minHeight: 24 }}>
              {categorias.map((c) => (
                <Pill key={c.id} COL={COL} bg={COL.blueBg} color={COL.infoText} onDelete={() => eliminarCat(c)}>{c.nombre}</Pill>
              ))}
              {categorias.length === 0 && <span style={{ fontSize: 12, color: COL.faint }}>Sin categorías. Crea la primera.</span>}
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${COL.border}` }} />

          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: COL.text, marginBottom: 4 }}>Unidades de medida</div>
            <div style={{ fontSize: 11.5, color: COL.faint, marginBottom: 10 }}>kg, L, unidad, caja… las que uses en tu negocio</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={newUnit} onChange={(e) => setNewUnit(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addUnit()} placeholder="Unidad (ej. Kilo)" style={{ ...inputStyle(COL), flex: 2 }} />
              <input value={newUnitSym} onChange={(e) => setNewUnitSym(e.target.value)} placeholder="Símbolo" style={{ ...inputStyle(COL), width: isMobile ? 72 : 96 }} />
              <Boton COL={COL} onClick={addUnit} disabled={guardando || !newUnit.trim()}>Añadir</Boton>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, minHeight: 24 }}>
              {unidades.map((u) => (
                <Pill key={u.id} COL={COL} bg={COL.greenBg} color={COL.successText} onDelete={() => eliminarUnit(u)}>{u.nombre}{u.simbolo ? ` (${u.simbolo})` : ''}</Pill>
              ))}
              {unidades.length === 0 && <span style={{ fontSize: 12, color: COL.faint }}>Sin unidades. Crea la primera.</span>}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 16, borderTop: `1px solid ${COL.border}`, marginTop: 4 }}>
          <Boton COL={COL} ghost onClick={onClose}>Cerrar</Boton>
        </div>
      </div>
    </Overlay>
  );
}

function Pill({ COL, bg, color, onDelete, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color, fontSize: 12, fontWeight: 600, padding: '4px 6px 4px 10px', borderRadius: 999 }}>
      <span>{children}</span>
      <button
        type="button"
        onClick={onDelete}
        title="Eliminar"
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(0,0,0,0.12)', color, padding: 0 }}
      >
        <FiX size={11} />
      </button>
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════
//  COMPONENTES BASE
// ════════════════════════════════════════════════════════════════════

function Overlay({ COL, onClose, children, isMobile }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: COL.overlay, zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ maxHeight: isMobile ? '92vh' : '90vh', overflowY: 'auto', width: '100%', maxWidth: 640 }}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ icon: Icon, title, subtitle, onClose, COL }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 18px 4px' }}>
      {Icon && (
        <div style={{ width: 38, height: 38, borderRadius: 10, background: COL.blueBg, color: COL.pri2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={19} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COL.text }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12.5, color: COL.muted, marginTop: 2 }}>{subtitle}</div>}
      </div>
      <IconBtn COL={COL} onClick={onClose}><FiX size={16} /></IconBtn>
    </div>
  );
}

function ModalFooter({ onClose, onSave, COL, label, guardando }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 16, position: 'sticky', bottom: 0, background: COL.surface, borderTop: `1px solid ${COL.border}` }}>
      <Boton COL={COL} ghost onClick={onClose} disabled={guardando}>Cancelar</Boton>
      <Boton COL={COL} onClick={onSave} disabled={guardando}>{guardando ? 'Guardando…' : (label || 'Guardar')}</Boton>
    </div>
  );
}

function Field({ label, icon: Icon, required, error, hint, COL, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
        {Icon && <Icon size={13} style={{ color: COL.muted, flexShrink: 0 }} />}
        <span style={{ fontSize: 12, fontWeight: 700, color: COL.muted, whiteSpace: 'nowrap' }}>{label}</span>
        {required && <span style={{ color: COL.red, fontSize: 12 }}>*</span>}
      </div>
      {children}
      {error ? (
        <span style={{ fontSize: 11.5, color: COL.red, display: 'flex', alignItems: 'center', gap: 4 }}>
          <FiAlertTriangle size={12} /> {error}
        </span>
      ) : hint ? (
        <span style={{ fontSize: 11, color: COL.faint, lineHeight: 1.4 }}>{hint}</span>
      ) : null}
    </div>
  );
}

function Section({ label, right, COL, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: COL.faint, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
        {right}
      </div>
      {children}
    </div>
  );
}

function Boton({ COL, ghost, onClick, children, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 14px',
        borderRadius: 8, border: ghost ? `1px solid ${COL.border}` : 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        background: ghost ? 'transparent' : COL.pri2, color: ghost ? COL.text : '#fff',
        fontSize: 13, fontWeight: 600, minHeight: 38, opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function IconBtn({ COL, danger, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer', background: danger ? COL.redBg : COL.bg2, color: danger ? COL.red : COL.text }}>
      {children}
    </button>
  );
}

function Tabla({ children }) {
  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 560 }}>{children}</table>
    </div>
  );
}

const TH = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: 'var(--agx-muted)', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid var(--agx-border)' };
const TD = { padding: '10px 12px', fontSize: 13, color: 'var(--agx-text)' };

function inputStyle(COL) {
  return { boxSizing: 'border-box', padding: '9px 10px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.inputBg, color: COL.text, fontSize: 13, width: '100%', minWidth: 0 };
}
function selectStyle(COL) {
  return { boxSizing: 'border-box', padding: '9px 10px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, fontSize: 13, width: '100%', minWidth: 0 };
}

const S = {
  card: { borderRadius: 12, border: '1px solid', padding: 14, display: 'flex', flexDirection: 'column', gap: 6 },
  badge: { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' },
  banner: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 },
  warnBanner: { background: 'var(--agx-warning-bg)', color: 'var(--agx-warning-text)' },
  modal: { borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden', margin: '0 auto' },
};
